
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Player, Team, Lineup, NoteSection, LiberoAssignment, RotationalSubstitution, GameEvent, isGameType, Organization, Season } from '../types';
import { Plus, User, Phone, Mail, ChevronRight, Save, X, Trash2, Camera, Check, Users, GripVertical, ArrowUp, ArrowDown, List, LayoutGrid, UserPlus, CheckSquare, Square, Star, Activity, ArrowUpDown, Shield, AlertTriangle, Loader2, ChevronDown, ChevronUp, Target, FileText, BarChart2, Swords, Ruler, Scale, Calendar, AlertCircle, FileCheck, Settings2, CheckCircle2, Circle, Notebook, FileEdit, Layers, StickyNote, Repeat, ArrowRight, ArrowLeftRight, Lock, ScanLine, Wand2, GraduationCap } from 'lucide-react';
import { supabase, mapPlayerFromDB, mapTeamFromDB, mapLineupFromDB } from '../services/supabase';
import { isDevUser } from '../lib/featureFlags';
import { formatHeight, UnitSystem } from '../lib/measurements';
import PlayerEditModal from './PlayerEditModal';
import LineupSheetScanner from './LineupSheetScanner';
import QuickLineupBuilder from './QuickLineupBuilder';
import RosterScanner, { ScannedPlayer } from './RosterScanner';
import SeasonSelector from './SeasonSelector';

interface TeamManagementProps {
  organizationId: string;
  teams: Team[];
  players: Player[];
  lineups?: Lineup[];
  events?: GameEvent[];
  setTeams: React.Dispatch<React.SetStateAction<Team[]>>;
  setPlayers: React.Dispatch<React.SetStateAction<Player[]>>;
  setLineups?: React.Dispatch<React.SetStateAction<Lineup[]>>;
  shouldAutoCreateTeam?: boolean;
  setShouldAutoCreateTeam?: (val: boolean) => void;
  allowTwoLiberos?: boolean;
  onAllowTwoLiberosChange?: (val: boolean) => void;
  currentOrg?: Organization | null;
  currentUserEmail?: string | null;
  initialActiveTeamId?: string;
  onActiveTeamChange?: (id: string) => void;
  unitSystem?: UnitSystem;
  seasons?: Season[];
  setSeasons?: React.Dispatch<React.SetStateAction<Season[]>>;
  onViewingSeasonChange?: (s: Season | null) => void;
}

const POSITIONS = ['Setter', 'Outside Hitter', 'Opposite', 'Middle Blocker', 'Libero', 'DS', 'Unknown'];
// "Right Side" is a legacy name for Opposite — treat both as the same group
const POSITION_ALIASES: Record<string, string[]> = {
  'Opposite': ['Opposite', 'Right Side'],
};

// Color themes for teams
const getTeamColor = (index: number) => {
  const colors = [
    { name: 'blue', bg: 'bg-blue-500', hover: 'hover:bg-blue-600', light: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', ring: 'ring-blue-500' },
    { name: 'emerald', bg: 'bg-emerald-500', hover: 'hover:bg-emerald-600', light: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-600', ring: 'ring-emerald-500' },
    { name: 'red', bg: 'bg-red-500', hover: 'hover:bg-red-600', light: 'bg-red-50', border: 'border-red-200', text: 'text-red-600', ring: 'ring-red-500' },
    { name: 'violet', bg: 'bg-violet-500', hover: 'hover:bg-violet-600', light: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-600', ring: 'ring-violet-500' },
    { name: 'amber', bg: 'bg-amber-500', hover: 'hover:bg-amber-600', light: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-600', ring: 'ring-amber-500' },
    { name: 'cyan', bg: 'bg-cyan-500', hover: 'hover:bg-cyan-600', light: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-600', ring: 'ring-cyan-500' },
  ];
  return colors[index % colors.length];
};

// Position abbreviation helper (mirrors GameTime's getPosAbbreviation)
const getPosAbbreviation = (pos: string) => {
  const map: Record<string, string> = {
    'Setter': 'S', 'Outside Hitter': 'OH', 'Right Side': 'Oppo', 'Opposite': 'Oppo',
    'Middle Blocker': 'MB', 'Libero': 'L', 'DS': 'DS', 'Unknown': '?'
  };
  return map[pos] || pos?.substring(0, 2).toUpperCase() || '?';
};

// Extracted LineupCard — matches GameTime's PlayerCard isLineupEditMode appearance
const LineupCard = ({ pos, label, assignedId, players, onClick }: { pos: string, label: string, assignedId: string, players: Player[], onClick: () => void }) => {
  const player = players.find(p => p.id === assignedId);

  return (
    <div className="relative flex flex-col rounded-xl border-2 transition-all shadow-sm overflow-hidden bg-white border-slate-700 h-40 md:h-48">
      {/* Header — mirrors GameTime PlayerCard header */}
      <div className="flex items-center px-1.5 py-1 border-b h-10 shrink-0 gap-1 border-black/5 bg-black/5">
        {player ? (
          <>
            <div className="flex items-center gap-1 shrink-0">
              {player.photoUrl ? (
                <div className="w-9 h-9 rounded-full border border-white shadow-sm overflow-hidden bg-slate-200">
                  <img src={player.photoUrl} alt="Player" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-9 h-9 rounded-full border border-white shadow-sm flex items-center justify-center text-sm font-bold bg-white text-slate-700">
                  {player.jerseyNumber || player.name.charAt(0)}
                </div>
              )}
              <span className="text-lg font-black leading-none text-slate-900 min-w-[1.5rem] text-center">
                {player.jerseyNumber || ''}
              </span>
            </div>
            <div className="flex-1 min-w-0 flex items-baseline gap-1 overflow-hidden">
              <span className="text-[17px] font-bold leading-none shrink-0 text-slate-900 truncate">{player.name}</span>
              <span className="text-[11px] font-semibold opacity-50 truncate text-slate-900">{player.position}{player.secondaryPosition ? ` / ${player.secondaryPosition}` : ''}</span>
            </div>
            <span className="text-[9px] font-black px-1.5 py-0.5 rounded text-white min-w-[1.5rem] text-center bg-slate-700">
              {getPosAbbreviation(player.position)}
            </span>
          </>
        ) : (
          <span className="text-[11px] italic text-slate-400">Empty</span>
        )}
      </div>

      {/* Body — mirrors GameTime PlayerCard isLineupEditMode body */}
      <button
        type="button"
        onClick={onClick}
        className="flex-1 flex flex-col relative overflow-hidden w-full transition-opacity active:opacity-80 hover:bg-slate-50"
      >
        <div className="absolute inset-0 w-full h-full flex items-center py-2 px-1">
          {/* Left: Roman numeral */}
          <div className="flex-1 flex items-center justify-center">
            <span className="text-4xl font-black select-none text-slate-300">{label}</span>
          </div>

          {/* Center: player circle or empty state */}
          {player ? (
            player.photoUrl ? (
              <div className="h-full aspect-square rounded-full overflow-hidden border-2 border-slate-200 shadow-md shrink-0">
                <img src={player.photoUrl} alt="" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="h-full aspect-square rounded-full flex items-center justify-center border-2 shadow-md shrink-0 bg-slate-100 text-slate-700 border-slate-200">
                <span className="text-4xl font-black select-none leading-none">{player.jerseyNumber || player.name.charAt(0)}</span>
              </div>
            )
          ) : (
            <div className="flex flex-col items-center gap-1 opacity-40 shrink-0">
              <UserPlus className="w-7 h-7 text-slate-400" />
              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">+ Assign</span>
            </div>
          )}

          {/* Right: empty flex spacer */}
          <div className="flex-1" />
        </div>
      </button>
    </div>
  );
};

const TeamManagement: React.FC<TeamManagementProps> = ({
  organizationId,
  teams,
  players,
  lineups = [],
  events = [],
  setTeams,
  setPlayers,
  setLineups,
  shouldAutoCreateTeam,
  setShouldAutoCreateTeam,
  allowTwoLiberos = false,
  onAllowTwoLiberosChange,
  currentOrg,
  currentUserEmail,
  initialActiveTeamId,
  onActiveTeamChange,
  unitSystem = 'imperial' as UnitSystem,
  seasons = [],
  setSeasons,
  onViewingSeasonChange,
}) => {
  const isCoach = currentOrg?.role === 'head_coach' || currentOrg?.role === 'assistant';
  const canSeeDevFeatures = isDevUser(currentUserEmail);
  const [teamTypeView, setTeamTypeView] = useState<'club' | 'opponent'>('club');
  const [activeTeamId, setActiveTeamId] = useState<string>(initialActiveTeamId || '');

  // Sync prop → local state only when our state hasn't been resolved yet (still '').
  // This handles the race where globalActiveTeamId resolves after first mount.
  useEffect(() => {
    if (initialActiveTeamId && activeTeamId === '') {
      setActiveTeamId(initialActiveTeamId);
    }
  }, [initialActiveTeamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [isAddingPlayer, setIsAddingPlayer] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [isAddingTeam, setIsAddingTeam] = useState(false);
  const [viewMode, setViewMode] = useState<'roster' | 'depth' | 'lineups' | 'eligibility' | 'notes'>('roster');
  const [sortBy, setSortBy] = useState<'manual' | 'name' | 'position' | 'number'>('manual');
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  
  // Lineup State
  const [editingLineup, setEditingLineup] = useState<Partial<Lineup> | null>(null);
  const [showLineupScanner, setShowLineupScanner] = useState(false);
  const [showRosterScanner, setShowRosterScanner] = useState(false);
  const [showQuickBuilder, setShowQuickBuilder] = useState(false);
  const [liberoConfig, setLiberoConfig] = useState<LiberoAssignment[]>([]);
  const [lineupLiberoCount, setLineupLiberoCount] = useState<1 | 2>(1);
  const [isSavingLineup, setIsSavingLineup] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [dbSaveError, setDbSaveError] = useState<string | null>(null);
  const [editingRotSub, setEditingRotSub] = useState<Partial<RotationalSubstitution> | null>(null);
  const [liberoOptionsModal, setLiberoOptionsModal] = useState<{ slot: number; replacesPlayerIds: string[]; servesForPlayerId: string } | null>(null);
  const [isSuggestedRotSub, setIsSuggestedRotSub] = useState(false);
  const [lineupSwapModal, setLineupSwapModal] = useState<{ pos: string } | null>(null);

  // Drag & Drop State
  const [draggedPlayerId, setDraggedPlayerId] = useState<string | null>(null);
  const [dragOverTeamId, setDragOverTeamId] = useState<string | null>(null);
  const [dragOverPosition, setDragOverPosition] = useState<string | null>(null);
  const [dragOverPlayerId, setDragOverPlayerId] = useState<string | null>(null);
  const [dragOverHalf, setDragOverHalf] = useState<'top' | 'bottom'>('bottom');
  
  // Beta team-count limit
  const [showBetaTeamLimit, setShowBetaTeamLimit] = useState(false);

  // Custom Delete Modal State
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ type: 'team' | 'player' | 'lineup', id: string, name: string } | null>(null);

  // Season State
  const [viewingSeason, setViewingSeason] = useState<Season | null>(null);
  const [showNewSeasonModal, setShowNewSeasonModal] = useState(false);
  const [selectedSeasonIndices, setSelectedSeasonIndices] = useState<number[]>([]);
  const [newSeasonYear, setNewSeasonYear] = useState(() => new Date().getFullYear());
  const [isSavingSeason, setIsSavingSeason] = useState(false);
  const [newSeasonError, setNewSeasonError] = useState<string | null>(null);
  // After a new season starts: offer to archive players whose grad year has passed.
  const [graduatedPrompt, setGraduatedPrompt] = useState<{ outgoingSeasonName: string; players: Player[] } | null>(null);
  const [selectedGraduatedIds, setSelectedGraduatedIds] = useState<Set<string>>(new Set());
  const [isArchivingGraduated, setIsArchivingGraduated] = useState(false);

  const handleViewingSeasonChange = (s: Season | null) => {
    setViewingSeason(s);
    onViewingSeasonChange?.(s);
  };

  // Coach Notes State
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const visibleTeams = useMemo(() => {
    return teams.filter(t => (t.type || 'club') === teamTypeView);
  }, [teams, teamTypeView]);

  const sortedVisibleTeams = useMemo(() => {
    return [...visibleTeams].sort((a, b) => a.name.localeCompare(b.name));
  }, [visibleTeams]);

  // Logic to handle "too many teams"
  const MAX_VISIBLE_TEAMS = 4;
  const topTeams = useMemo(() => {
     // Always include the currently active team in the top buttons if it exists
     const top = sortedVisibleTeams.slice(0, MAX_VISIBLE_TEAMS);
     const isActiveInTop = top.some(t => t.id === activeTeamId);
     const activeTeamObj = sortedVisibleTeams.find(t => t.id === activeTeamId);
     
     if (!isActiveInTop && activeTeamObj && activeTeamId !== 'unassigned') {
        return [...top.slice(0, MAX_VISIBLE_TEAMS - 1), activeTeamObj];
     }
     return top;
  }, [sortedVisibleTeams, activeTeamId]);

  const otherTeams = useMemo(() => {
     const topIds = new Set(topTeams.map(t => t.id));
     return sortedVisibleTeams.filter(t => !topIds.has(t.id));
  }, [sortedVisibleTeams, topTeams]);

  useEffect(() => {
    // If current selection is valid, do nothing
    if (activeTeamId === 'unassigned') {
        // Lineup view does not allow unassigned
        if (viewMode === 'lineups' && visibleTeams.length > 0) {
            const fav = visibleTeams.find(t => t.isFavorite) || visibleTeams.find(t => t.isDefault);
            setActiveTeamId(fav ? fav.id : visibleTeams[0].id);
        }
        return;
    }
    const teamExists = visibleTeams.some(t => t.id === activeTeamId);
    if (teamExists) return;

    // Default logic if selection invalid — prefer starred team, then first
    if (visibleTeams.length > 0) {
        const def = visibleTeams.find(t => t.isFavorite) || visibleTeams.find(t => t.isDefault);
        setActiveTeamId(def ? def.id : visibleTeams[0].id);
    } else {
        setActiveTeamId('unassigned');
    }
  }, [visibleTeams, activeTeamId, teamTypeView, viewMode]);

  useEffect(() => {
    if (shouldAutoCreateTeam) {
      setIsAddingTeam(true);
      if (setShouldAutoCreateTeam) setShouldAutoCreateTeam(false);
    }
  }, [shouldAutoCreateTeam, setShouldAutoCreateTeam]);

  // Clean up timeouts
  useEffect(() => {
    return () => {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const BETA_TEAM_LIMIT = 2;

  const handleAddTeam = async () => {
    if (!newTeamName.trim()) return;

    // Beta limit: max 2 club teams per org (dev org and beta testers are exempt)
    if (organizationId !== 'dev-org-id' && !currentOrg?.isBetaTester && teamTypeView === 'club') {
      const clubTeamCount = teams.filter(t => (t.type || 'club') === 'club').length;
      if (clubTeamCount >= BETA_TEAM_LIMIT) {
        setIsAddingTeam(false);
        setNewTeamName('');
        setShowBetaTeamLimit(true);
        return;
      }
    }
    
    // Optimistic Update
    const tempId = organizationId === 'dev-org-id' ? `dev-team-${Date.now()}` : `temp-team-${Date.now()}`;
    const newTeam: Team = { 
        id: tempId, 
        organizationId, 
        name: newTeamName, 
        isDefault: false, 
        isFavorite: false,
        type: teamTypeView,
        coachNotes: []
    };
    
    setTeams([...teams, newTeam]); 
    setActiveTeamId(newTeam.id); 
    setNewTeamName(''); 
    setIsAddingTeam(false);

    if (organizationId === 'dev-org-id') return;

    try {
      const { data, error } = await supabase
        .from('teams')
        .insert([{ name: newTeamName, organization_id: organizationId, is_default: false, type: teamTypeView }])
        .select()
        .single();
        
      if (data && !error) {
        const realTeam = mapTeamFromDB(data);
        // Replace temp team with real one
        setTeams(prev => prev.map(t => t.id === tempId ? realTeam : t));
        setActiveTeamId(realTeam.id);
      }
    } catch (error) { 
        console.error("Error adding team", error); 
        // Revert on error
        setTeams(prev => prev.filter(t => t.id !== tempId));
    }
  };

  const initiateDeleteTeam = async () => {
    if (!activeTeamId || activeTeamId === 'unassigned') return;
    const teamToDelete = teams.find(t => t.id === activeTeamId);
    if (!teamToDelete) return;

    const teamHasPlayers = players.some(p => p.teamId === activeTeamId);
    if (!teamHasPlayers) {
        // Empty team: hard delete immediately (no data at risk)
        executeDeleteTeam(teamToDelete.id, false);
    } else {
        setDeleteConfirmation({ type: 'team', id: teamToDelete.id, name: teamToDelete.name });
    }
  };

  const initiateDeletePlayer = (e: React.MouseEvent, playerId: string) => {
     e.stopPropagation();
     const player = players.find(p => p.id === playerId);
     if (!player) return;
     setDeleteConfirmation({ type: 'player', id: player.id, name: player.name });
  };

  const initiateDeleteLineup = (e: React.MouseEvent, id: string) => {
     e.stopPropagation();
     const lineup = lineups?.find(l => l.id === id);
     if (!lineup) return;
     setDeleteConfirmation({ type: 'lineup', id, name: lineup.name });
  };

  const executeDeleteTeam = async (id: string, archive = true) => {
    setTeams(prev => prev.filter(t => t.id !== id));
    setDeleteConfirmation(null);
    setActiveTeamId('unassigned');

    if (archive) {
      // Soft-delete: players stay on the team
      if (organizationId === 'dev-org-id') return;
      try {
        await supabase.from('teams').update({ is_archived: true }).eq('id', id);
      } catch (error) { console.error("Error archiving team", error); }
    } else {
      // Hard delete: empty team, no players to worry about
      if (organizationId === 'dev-org-id') return;
      try {
        await supabase.from('teams').delete().eq('id', id);
      } catch (error) { console.error("Error deleting team", error); }
    }
  };

  const executeDeletePlayer = async (id: string, archiveToSeason = false) => {
    const player = players.find(p => p.id === id);
    setPlayers(prev => prev.filter(p => p.id !== id));
    setDeleteConfirmation(null);

    if (archiveToSeason && player && setSeasons) {
      const activeSeason = seasons.find(s => s.isActive);
      if (activeSeason) {
        const existing: Player[] = activeSeason.rosterSnapshot || [];
        const already = existing.some(p => p.id === id);
        if (!already) {
          const updated = [...existing, player];
          if (organizationId !== 'dev-org-id') {
            await supabase.from('seasons').update({ roster_snapshot: updated }).eq('id', activeSeason.id);
          }
          setSeasons(prev => prev.map(s => s.id === activeSeason.id ? { ...s, rosterSnapshot: updated } : s));
        }
      }
    }

    if (organizationId === 'dev-org-id') return;
    try { await supabase.from('players').delete().eq('id', id); } catch (error) { console.error("Error deleting player", error); }
  };

  const SEASONS = ['Spring', 'Summer', 'Fall', 'Winter'];

  const getSeasonName = (indices: number[], year: number): string => {
    if (indices.length === 0) return '';
    const includesWinter = indices.includes(3);
    const suffix = includesWinter ? `${year}-${year + 1}` : `${year}`;
    return `${indices.map(i => SEASONS[i]).join('/')} ${suffix}`;
  };

  const getSeasonDates = (indices: number[], year: number): { startDate: string; endDate: string } => {
    const starts = [`${year}-03-01`, `${year}-06-01`, `${year}-09-01`, `${year}-12-01`];
    const febEnd = String(new Date(year + 1, 2, 0).getDate()).padStart(2, '0');
    const ends = [`${year}-05-31`, `${year}-08-31`, `${year}-11-30`, `${year + 1}-02-${febEnd}`];
    const first = Math.min(...indices), last = Math.max(...indices);
    return { startDate: starts[first], endDate: ends[last] };
  };

  const handleSeasonPillClick = (i: number) => {
    setSelectedSeasonIndices(prev => {
      if (prev.length === 0) return [i];
      const min = Math.min(...prev), max = Math.max(...prev);
      if (prev.includes(i)) {
        if (min === max) return [];
        if (i === min || i === max) return prev.filter(x => x !== i);
        return [i];
      }
      if (i === min - 1) return Array.from({ length: max - i + 1 }, (_, k) => i + k);
      if (i === max + 1) return Array.from({ length: i - min + 1 }, (_, k) => min + k);
      return [i];
    });
  };

  // Players whose graduation year has passed relative to the new season's start.
  // Grad year Y students finish in late spring of Y, so a season starting June or
  // later in year Y (Summer/Fall/Winter) already counts them as graduated; a Spring
  // season in year Y does not. Non-numeric grad years ("Freshman") are ignored.
  const getGraduatedPlayers = (newSeasonStartDate: string): Player[] => {
    const startYear = Number(newSeasonStartDate.slice(0, 4));
    const startMonth = Number(newSeasonStartDate.slice(5, 7));
    if (!startYear) return [];
    const cutoffYear = startMonth >= 6 ? startYear : startYear - 1;
    return players.filter(p => {
      const gy = Number(String(p.gradYear ?? '').trim());
      return Number.isInteger(gy) && gy >= 1900 && gy <= cutoffYear;
    });
  };

  const openGraduatedPrompt = (outgoingSeason: Season | undefined, newSeasonStartDate: string) => {
    // Only offer when the outgoing season snapshot now holds these players, so
    // archiving genuinely preserves their history.
    if (!outgoingSeason) return;
    const graduated = getGraduatedPlayers(newSeasonStartDate)
      .sort((a, b) => Number(a.gradYear) - Number(b.gradYear) || a.name.localeCompare(b.name));
    if (graduated.length === 0) return;
    setGraduatedPrompt({ outgoingSeasonName: outgoingSeason.name, players: graduated });
    setSelectedGraduatedIds(new Set(graduated.map(p => p.id)));
  };

  const handleArchiveGraduated = async () => {
    if (!graduatedPrompt || isArchivingGraduated) return;
    const ids = graduatedPrompt.players.map(p => p.id).filter(id => selectedGraduatedIds.has(id));
    if (ids.length === 0) { setGraduatedPrompt(null); return; }
    setIsArchivingGraduated(true);
    try {
      // Their roster data already lives in the outgoing season's snapshot, so
      // archiving only removes them from the active roster.
      if (organizationId !== 'dev-org-id') {
        const { error } = await supabase.from('players').delete().in('id', ids);
        if (error) throw error;
      }
      setPlayers(prev => prev.filter(p => !ids.includes(p.id)));
      setGraduatedPrompt(null);
    } catch (e) {
      console.error('Failed to archive graduated players:', e);
    } finally {
      setIsArchivingGraduated(false);
    }
  };

  const handleStartNewSeason = async () => {
    if (selectedSeasonIndices.length === 0 || isSavingSeason || !setSeasons) return;
    setIsSavingSeason(true);
    try {
      const activeSeason = seasons.find(s => s.isActive);
      const today = new Date().toISOString().slice(0, 10);
      const seasonName = getSeasonName(selectedSeasonIndices, newSeasonYear);
      const { startDate, endDate } = getSeasonDates(selectedSeasonIndices, newSeasonYear);

      if (organizationId === 'dev-org-id') {
        // Dev mode: local state only
        const newId = `dev-season-${Date.now()}`;
        if (activeSeason) {
          setSeasons(prev => prev.map(s => s.isActive ? { ...s, isActive: false, endDate: today, rosterSnapshot: players } : s));
        }
        setSeasons(prev => [{ id: newId, organizationId, name: seasonName, startDate, endDate, isActive: true, rosterSnapshot: null }, ...prev.filter(s => !s.isActive || s.id !== activeSeason?.id)]);
        setShowNewSeasonModal(false);
        setSelectedSeasonIndices([]);
        setNewSeasonYear(new Date().getFullYear());
        setIsSavingSeason(false);
        openGraduatedPrompt(activeSeason, startDate);
        return;
      }

      // Archive current season with roster snapshot
      if (activeSeason) {
        await supabase.from('seasons').update({ is_active: false, end_date: today, roster_snapshot: players }).eq('id', activeSeason.id);
      }

      // Create new season
      const { data, error } = await supabase.from('seasons').insert({
        organization_id: organizationId,
        name: seasonName,
        start_date: startDate,
        end_date: endDate,
        is_active: true,
      }).select().single();

      if (error) throw error;

      const newSeason = { id: data.id, organizationId, name: data.name, startDate: data.start_date, endDate: data.end_date, isActive: true, rosterSnapshot: null, createdAt: data.created_at };
      setSeasons(prev => [newSeason, ...prev.map(s => s.id === activeSeason?.id ? { ...s, isActive: false, endDate: today, rosterSnapshot: players } : s)]);
      handleViewingSeasonChange(null);
      setShowNewSeasonModal(false);
      setSelectedSeasonIndices([]);
      setNewSeasonYear(new Date().getFullYear());
      setNewSeasonError(null);
      openGraduatedPrompt(activeSeason, startDate);
    } catch (e: any) {
      console.error('Failed to start new season:', e);
      setNewSeasonError(e?.message ?? 'Something went wrong. Please try again.');
    } finally {
      setIsSavingSeason(false);
    }
  };

  const executeDeleteLineup = async (id: string) => {
     if (setLineups) setLineups(prev => prev.filter(l => l.id !== id));
     setDeleteConfirmation(null);
     if (organizationId === 'dev-org-id') return;
     try { await supabase.from('lineups').delete().eq('id', id); } catch(e) { console.error(e); }
  };

  const handleToggleLineupFavorite = async (e: React.MouseEvent, lineupId: string) => {
    e.stopPropagation();
    if (!setLineups) return;
    const lineup = lineups?.find(l => l.id === lineupId);
    if (!lineup) return;
    const nowFavorite = !lineup.isFavorite;
    // Optimistic update
    setLineups(prev => prev.map(l => {
      if (l.teamId !== activeTeamId) return l;
      return { ...l, isFavorite: l.id === lineupId ? nowFavorite : false, isDefault: l.id === lineupId ? nowFavorite : false };
    }));
    if (organizationId === 'dev-org-id') return;
    try {
      // Clear all others for this team, then set this one
      const { error: clearErr } = await supabase.from('lineups').update({ is_favorite: false, is_default: false }).eq('team_id', activeTeamId);
      if (clearErr) throw clearErr;
      if (nowFavorite) {
        const { error: setErr } = await supabase.from('lineups').update({ is_favorite: true, is_default: true }).eq('id', lineupId);
        if (setErr) throw setErr;
      }
    } catch(err) {
      console.error('Failed to save lineup favorite:', err);
      // Revert optimistic update
      setLineups(prev => prev.map(l => l.teamId !== activeTeamId ? l : { ...l, isFavorite: l.id === lineupId ? lineup.isFavorite : l.isFavorite, isDefault: l.id === lineupId ? lineup.isDefault : l.isDefault }));
    }
  };


  const TEAM_PLAYER_CAP = 20;

  // Player Create/Update Handlers
  const handleOpenCreatePlayer = () => {
      // Enforce 20-player cap on real teams (not the unassigned pool)
      if (activeTeamId !== 'unassigned') {
          const teamPlayerCount = players.filter(p => p.teamId === activeTeamId).length;
          if (teamPlayerCount >= TEAM_PLAYER_CAP) return;
      }
      // Create a skeleton player object for the modal
      const newPlayer: Player = {
          id: `new-${Date.now()}`,
          organizationId,
          teamId: activeTeamId === 'unassigned' ? 'unassigned' : activeTeamId,
          name: '',
          jerseyNumber: '',
          position: 'Unknown', // Default to Unknown as requested
          rank: 0
      };
      setSelectedPlayer(newPlayer);
      setIsAddingPlayer(true);
  };

  const handleOpenEditPlayer = (player: Player) => {
      setSelectedPlayer(player);
      setIsAddingPlayer(true);
  };

  const handlePlayerSave = (updatedPlayer: Player) => {
      const exists = players.some(p => p.id === updatedPlayer.id);
      if (!exists) {
          setPlayers(prev => [...prev, updatedPlayer]);
      } else {
          setPlayers(prev => prev.map(p => p.id === updatedPlayer.id ? updatedPlayer : p));
      }
      setIsAddingPlayer(false);
      setSelectedPlayer(null);
  };

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  const handleBatchImportPlayers = async (scannedPlayers: ScannedPlayer[]): Promise<void> => {
    if (organizationId === 'dev-org-id') {
      const newPlayers: Player[] = scannedPlayers.map((sp, idx) => ({
        id: `dev-scan-${Date.now()}-${idx}`,
        organizationId,
        teamId: activeTeamId === 'unassigned' ? 'unassigned' : activeTeamId,
        name: sp.name,
        jerseyNumber: sp.jerseyNumber,
        position: sp.position,
        heightCm: sp.heightCm ?? undefined,
        weightKg: sp.weightKg ?? undefined,
        handedness: (sp.handedness as Player['handedness']) || undefined,
        gradYear: sp.gradYear || undefined,
        rank: 0,
      }));
      setPlayers(prev => [...prev, ...newPlayers]);
      setShowRosterScanner(false);
      return;
    }

    const rows = scannedPlayers.map(sp => ({
      organization_id: organizationId,
      team_id: uuidRegex.test(activeTeamId) ? activeTeamId : null,
      name: sp.name.trim(),
      jersey_number: sp.jerseyNumber || null,
      position: sp.position || 'Unknown',
      height_cm: sp.heightCm ?? null,
      weight_kg: sp.weightKg ?? null,
      handedness: sp.handedness || null,
      grad_year: sp.gradYear || null,
    }));

    const { data, error } = await supabase.from('players').insert(rows).select();
    if (error) throw error;
    setPlayers(prev => [...prev, ...data.map(mapPlayerFromDB)]);
    setShowRosterScanner(false);
  };

  // Drag and Drop Logic
  const handleDragStart = (e: React.DragEvent, playerId: string) => {
      setDraggedPlayerId(playerId);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', playerId); // required by most browsers to initiate drag
  };

  const handleDragEnd = () => {
      setDraggedPlayerId(null);
      setDragOverPosition(null);
      setDragOverPlayerId(null);
  };

  const handleDepthDragOver = (e: React.DragEvent, position: string) => {
      e.preventDefault();
      setDragOverPosition(position);
      setDragOverPlayerId(null); // hovering column gap, not a specific player
  };

  const handlePlayerDragOver = (e: React.DragEvent, position: string, playerId: string) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOverPosition(position);
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setDragOverPlayerId(playerId);
      setDragOverHalf(e.clientY < rect.top + rect.height / 2 ? 'top' : 'bottom');
  };

  const handleDepthDrop = async (e: React.DragEvent, targetPosition: string, targetPlayerId?: string) => {
      e.preventDefault();
      e.stopPropagation();

      const currentDragOverHalf = dragOverHalf; // capture before clearing
      setDragOverPosition(null);
      setDragOverPlayerId(null);

      if (!draggedPlayerId) return;
      const draggedPlayer = players.find(p => p.id === draggedPlayerId);
      if (!draggedPlayer) return;

      const aliases = POSITION_ALIASES[targetPosition] || [targetPosition];
      // Dropping into a column always makes that column the player's PRIMARY position.
      // Only the primary position decides which column a player lives in, so a
      // secondary-position match must not short-circuit the move.
      const isPositionChange = !aliases.includes(draggedPlayer.position);
      // If the target was already their secondary position, swap so the old primary
      // becomes the secondary (the player keeps both roles, just reordered).
      const targetWasSecondary = isPositionChange && aliases.includes(draggedPlayer.secondaryPosition || '');
      const newSecondaryPosition = targetWasSecondary ? draggedPlayer.position : draggedPlayer.secondaryPosition;

      let newPlayersList = [...players];

      if (isPositionChange) {
          newPlayersList = newPlayersList.map(p =>
              p.id === draggedPlayerId ? { ...p, position: targetPosition, secondaryPosition: newSecondaryPosition, rank: 999 } : p
          );
          if (organizationId !== 'dev-org-id') {
              const positionUpdate: Record<string, string | null> = { position: targetPosition };
              if (targetWasSecondary) positionUpdate.secondary_position = newSecondaryPosition || null;
              await supabase.from('players').update(positionUpdate).eq('id', draggedPlayerId);
          }
      }

      // Build ordered list for this position column (primary position only)
      const posPlayers = newPlayersList
          .filter(p => p.teamId === activeTeamId && aliases.includes(p.position))
          .sort((a, b) => (a.rank || 0) - (b.rank || 0));

      const sourceIndex = posPlayers.findIndex(p => p.id === draggedPlayerId);

      if (sourceIndex !== -1 || isPositionChange) {
          const without = posPlayers.filter(p => p.id !== draggedPlayerId);
          const moved = newPlayersList.find(p => p.id === draggedPlayerId)!;

          let insertIndex: number;
          if (targetPlayerId && targetPlayerId !== draggedPlayerId) {
              const idx = without.findIndex(p => p.id === targetPlayerId);
              // top half → insert before; bottom half → insert after
              insertIndex = idx === -1 ? without.length : (currentDragOverHalf === 'bottom' ? idx + 1 : idx);
          } else {
              insertIndex = without.length;
          }
          without.splice(insertIndex, 0, moved);

          const updates = without.map((p, idx) => ({ id: p.id, rank: idx + 1 }));

          setPlayers(prev => prev.map(p => {
              const update = updates.find(u => u.id === p.id);
              const positionPatch = p.id === draggedPlayerId && isPositionChange
                  ? { position: targetPosition, secondaryPosition: newSecondaryPosition }
                  : {};
              if (update) return { ...p, ...positionPatch, rank: update.rank };
              if (p.id === draggedPlayerId) return { ...p, ...positionPatch };
              return p;
          }));

          if (organizationId !== 'dev-org-id') {
              for (const u of updates) {
                  await supabase.from('players').update({ rank: u.rank }).eq('id', u.id);
              }
          }
      }

      setDraggedPlayerId(null);
  };

  const handleDragOverTeam = (e: React.DragEvent, teamId: string) => {
      e.preventDefault();
      if (teamId !== activeTeamId) {
          setDragOverTeamId(teamId);
      }
  };

  const handleDropPlayer = async (targetTeamId: string) => {
      if (!draggedPlayerId || targetTeamId === activeTeamId) {
          setDragOverTeamId(null);
          return;
      }
      
      // Update local state
      const targetIdToSave = targetTeamId === 'unassigned' ? null : targetTeamId;
      setPlayers(prev => prev.map(p => p.id === draggedPlayerId ? { ...p, teamId: targetTeamId } : p));
      
      const pid = draggedPlayerId;
      setDraggedPlayerId(null);
      setDragOverTeamId(null);

      // Persist to Supabase
      if (organizationId !== 'dev-org-id') {
          try {
            await supabase.from('players').update({ team_id: targetIdToSave }).eq('id', pid);
          } catch(e) { console.error("Error moving player", e); }
      }
  };

  const handleAssignPlayerToTeam = async (playerId: string, targetTeamId: string) => {
    const targetIdToSave = targetTeamId === 'unassigned' ? null : targetTeamId;
    setPlayers(prev => prev.map(p => p.id === playerId ? { ...p, teamId: targetTeamId } : p));
    if (organizationId !== 'dev-org-id') {
      try {
        await supabase.from('players').update({ team_id: targetIdToSave }).eq('id', playerId);
      } catch(e) { console.error("Error assigning player to team", e); }
    }
  };

  const handleSavePlayerNotes = async (playerId: string, notes: string) => {
    const player = players.find(p => p.id === playerId);
    const trimmed = notes.trim();
    // No-op if unchanged
    if (player && (player.notes || '') === trimmed) return;

    setPlayers(prev => prev.map(p => p.id === playerId ? { ...p, notes: trimmed } : p));
    if (organizationId !== 'dev-org-id') {
      try {
        await supabase.from('players').update({ notes: trimmed || null }).eq('id', playerId);
      } catch(e) { console.error("Error saving player notes", e); }
    }
  };

  const toggleNotesExpand = (playerId: string) => {
    setExpandedNotes(prev => {
      const next = new Set(prev);
      next.has(playerId) ? next.delete(playerId) : next.add(playerId);
      return next;
    });
  };

  // Lineup Handlers
  const handleCreateLineup = () => {
      setDbSaveError(null);
      setEditingLineup({ name: '', positions: { p1: '', p2: '', p3: '', p4: '', p5: '', p6: '' }, isDefault: false, rotationalSubstitutions: [] });
      setLiberoConfig([]);
      setLineupLiberoCount(1);
      setEditingRotSub(null);
      setIsSuggestedRotSub(false);
      setSaveError(false);
  };

  const handleScanIntoNewLineup = () => {
      setDbSaveError(null);
      setEditingLineup({ name: '', positions: { p1: '', p2: '', p3: '', p4: '', p5: '', p6: '' }, isDefault: false, rotationalSubstitutions: [] });
      setLiberoConfig([]);
      setLineupLiberoCount(1);
      setEditingRotSub(null);
      setIsSuggestedRotSub(false);
      setSaveError(false);
      setShowLineupScanner(true);
  };

  const handleEditLineup = (lineup: Lineup) => {
      setDbSaveError(null);
      setEditingLineup({ ...lineup });
      const assignments = lineup.liberoAssignments || [];
      setLiberoConfig(assignments);
      setLineupLiberoCount(assignments.length >= 2 ? 2 : 1);
      setEditingRotSub(null);
      setIsSuggestedRotSub(false);
      setSaveError(false);
  };

  const handleSaveLiberoOptions = () => {
      if (!liberoOptionsModal) return;
      const { slot, replacesPlayerIds, servesForPlayerId } = liberoOptionsModal;
      setLiberoConfig(prev => {
          const next = [...prev];
          if (next[slot]) next[slot] = { ...next[slot], replacesPlayerIds, servesForPlayerId: servesForPlayerId || undefined };
          return next;
      });
      setLiberoOptionsModal(null);
  };

  const handleSaveLineup = async () => {
      if (!editingLineup) return;
      
      if (!editingLineup.name || editingLineup.name.trim() === '') {
          setSaveError(true);
          return;
      }
      
      if (!setLineups) return;

      setIsSavingLineup(true);

      const newLineup: Lineup = {
          id: editingLineup.id || (organizationId === 'dev-org-id' ? `dev-lineup-${Date.now()}` : `temp-lineup-${Date.now()}`),
          organizationId,
          teamId: activeTeamId,
          name: editingLineup.name,
          positions: editingLineup.positions as any,
          liberoAssignments: liberoConfig,
          rotationalSubstitutions: editingLineup.rotationalSubstitutions || [],
          system: editingLineup.system || '5-1',
          dualRoleSetterIds: editingLineup.dualRoleSetterIds || [],
          isDefault: editingLineup.isDefault || false,
          isFavorite: editingLineup.isFavorite || false,
          createdAt: editingLineup.createdAt || new Date().toISOString()
      };

      // Snapshot for revert on error
      const isExisting = !!(editingLineup.id && !editingLineup.id.startsWith('temp-'));

      // Optimistic Update
      if (editingLineup.id) {
          setLineups(prev => prev.map(l => l.id === newLineup.id ? newLineup : l));
      } else {
          setLineups(prev => [...prev, newLineup]);
      }

      setEditingLineup(null);
      setEditingRotSub(null);
      setIsSuggestedRotSub(false);

      if (organizationId === 'dev-org-id') {
          setIsSavingLineup(false);
          return;
      }

      const isColError = (e: any) => e?.code === '42703' || (e?.message || '').includes('column');

      const doSave = async (includeRotSubs: boolean, includeSystemCols: boolean) => {
          const payload: Record<string, any> = {
              organization_id: organizationId,
              team_id: activeTeamId,
              name: newLineup.name,
              positions: newLineup.positions,
              libero_assignments: liberoConfig,
              is_default: newLineup.isDefault,
          };
          if (includeRotSubs) payload.rotational_substitutions = newLineup.rotationalSubstitutions || [];
          if (includeSystemCols) {
              payload.system = newLineup.system;
              payload.dual_role_setter_ids = (newLineup.dualRoleSetterIds?.length ?? 0) > 0 ? newLineup.dualRoleSetterIds : null;
          }

          if (editingLineup.id && !editingLineup.id.startsWith('temp-')) {
              const { data, error } = await supabase.from('lineups').update(payload).eq('id', editingLineup.id).select();
              if (error) throw error;
              if (data && data[0]) {
                  const updated = mapLineupFromDB(data[0]);
                  if (!includeRotSubs) updated.rotationalSubstitutions = newLineup.rotationalSubstitutions || [];
                  if (!includeSystemCols) { updated.system = newLineup.system; updated.dualRoleSetterIds = newLineup.dualRoleSetterIds; }
                  setLineups(prev => prev.map(l => l.id === editingLineup.id ? updated : l));
              }
          } else {
              const { data, error } = await supabase.from('lineups').insert([payload]).select();
              if (error) throw error;
              if (data && data[0]) {
                  const saved = mapLineupFromDB(data[0]);
                  if (!includeRotSubs) saved.rotationalSubstitutions = newLineup.rotationalSubstitutions || [];
                  if (!includeSystemCols) { saved.system = newLineup.system; saved.dualRoleSetterIds = newLineup.dualRoleSetterIds; }
                  setLineups(prev => prev.map(l => l.id === newLineup.id ? saved : l));
              }
          }
      };

      try {
          setDbSaveError(null);
          try {
              await doSave(true, true);
          } catch (e: any) {
              if (isColError(e)) {
                  try {
                      await doSave(false, true);
                  } catch (e2: any) {
                      if (isColError(e2)) await doSave(false, false);
                      else throw e2;
                  }
              } else {
                  throw e;
              }
          }
      } catch (e: any) {
          console.error("Error saving lineup to DB", e);
          // Revert optimistic update
          if (isExisting) {
              // Restore the pre-edit version (newLineup has our changes; editingLineup in closure has original values)
              const restored: Lineup = {
                  id: editingLineup.id!,
                  organizationId: editingLineup.organizationId || organizationId,
                  teamId: editingLineup.teamId || activeTeamId,
                  name: editingLineup.name || '',
                  positions: editingLineup.positions as any,
                  liberoAssignments: editingLineup.liberoAssignments || [],
                  rotationalSubstitutions: editingLineup.rotationalSubstitutions || [],
                  system: editingLineup.system || '5-1',
                  dualRoleSetterIds: editingLineup.dualRoleSetterIds || [],
                  isDefault: editingLineup.isDefault || false,
                  isFavorite: editingLineup.isFavorite || false,
                  createdAt: editingLineup.createdAt || '',
              };
              setLineups(prev => prev.map(l => l.id === restored.id ? restored : l));
          } else {
              setLineups(prev => prev.filter(l => l.id !== newLineup.id));
          }
          setDbSaveError(e?.message || 'Failed to save lineup. Please try again.');
      } finally {
          setIsSavingLineup(false);
      }
  };
  
  const handleLineupAssignment = (pos: string, id: string) => { 
      if (editingLineup && editingLineup.positions) { 
          const currentPositions = { ...editingLineup.positions } as Record<string, string>;
          
          // Duplicate Prevention: If moving a player, clear their old position
          if (id) {
              Object.keys(currentPositions).forEach(key => {
                  if (currentPositions[key] === id && key !== pos) {
                      currentPositions[key] = '';
                  }
              });
          }
          
          currentPositions[pos] = id;
          setEditingLineup({ ...editingLineup, positions: currentPositions as any }); 
      } 
  };

  // Notes Logic
  const activeTeam = teams.find(t => t.id === activeTeamId);
  
  const addNoteSection = () => {
      if (!activeTeam || !newSectionTitle.trim()) return;
      const newSection: NoteSection = { id: Date.now().toString(), title: newSectionTitle, content: '' };
      const updatedNotes = [...(activeTeam.coachNotes || []), newSection];
      
      updateTeamNotes(activeTeam.id, updatedNotes);
      setNewSectionTitle('');
  };

  const updateNoteContent = (sectionId: string, content: string) => {
      if (!activeTeam) return;
      const updatedNotes = (activeTeam.coachNotes || []).map(n => n.id === sectionId ? { ...n, content } : n);
      
      // Optimistic update
      setTeams(prev => prev.map(t => t.id === activeTeam.id ? { ...t, coachNotes: updatedNotes } : t));
      
      // Debounced Save
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
          persistTeamNotes(activeTeam.id, updatedNotes);
      }, 1000);
  };

  const updateTeamNotes = (teamId: string, notes: NoteSection[]) => {
      setTeams(prev => prev.map(t => t.id === teamId ? { ...t, coachNotes: notes } : t));
      persistTeamNotes(teamId, notes);
  };

  const persistTeamNotes = async (teamId: string, notes: NoteSection[]) => {
      if (organizationId === 'dev-org-id') return;
      try {
          await supabase.from('teams').update({ coach_notes: notes }).eq('id', teamId);
      } catch (e) { console.error("Error saving notes", e); }
  };

  const deleteNoteSection = (sectionId: string) => {
      if (!activeTeam || !window.confirm("Delete this section?")) return;
      const updatedNotes = (activeTeam.coachNotes || []).filter(n => n.id !== sectionId);
      updateTeamNotes(activeTeam.id, updatedNotes);
  };

  const currentTeamPlayers = players.filter(p => { 
      if (activeTeamId === 'unassigned') { return p.teamId === 'unassigned' || !p.teamId; } 
      return p.teamId === activeTeamId; 
  });
  
  const sortedPlayers = useMemo(() => { 
      const list = [...currentTeamPlayers]; 
      switch (sortBy) { 
          case 'name': return list.sort((a, b) => a.name.localeCompare(b.name)); 
          case 'position': return list.sort((a, b) => a.position.localeCompare(b.position));
          case 'number': return list.sort((a, b) => parseInt(a.jerseyNumber || '0') - parseInt(b.jerseyNumber || '0'));
          default: return list.sort((a, b) => (a.rank || 0) - (b.rank || 0)); 
      } 
  }, [currentTeamPlayers, sortBy]);
  
  const teamLineups = useMemo(() => {
    const raw = lineups?.filter(l => l.teamId === activeTeamId) || [];
    return [...raw].sort((a, b) => {
      if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [lineups, activeTeamId]);

  // Schedule opponents not yet registered as opponent teams
  const scheduleOnlyOpponents = useMemo(() => {
    const registeredNames = new Set(
      teams.filter(t => t.type === 'opponent').map(t => t.name.toLowerCase())
    );
    const seen = new Set<string>();
    return (events || [])
      .filter(e => isGameType(e.type) && e.opponent && !registeredNames.has(e.opponent.toLowerCase()))
      .reduce<string[]>((acc, e) => {
        const name = e.opponent!;
        if (!seen.has(name.toLowerCase())) { seen.add(name.toLowerCase()); acc.push(name); }
        return acc;
      }, []);
  }, [events, teams]);

  const handleRegisterOpponent = (name: string) => {
    setNewTeamName(name);
    // Temporarily force opponent type and call add directly
    const prevView = teamTypeView;
    setTeamTypeView('opponent');
    setTimeout(() => setIsAddingTeam(true), 0);
  };

  return (
    <div className="space-y-6 pb-20">
      
      {/* Player Edit Modal */}
      {isAddingPlayer && selectedPlayer && (
          <PlayerEditModal
            player={selectedPlayer}
            isOpen={true}
            onClose={() => { setIsAddingPlayer(false); setSelectedPlayer(null); }}
            onUpdate={handlePlayerSave}
            organizationId={organizationId}
            isCoach={isCoach}
            teams={teams.filter(t => (t.type || 'club') === 'club')}
            unitSystem={unitSystem}
          />
      )}

      {/* Roster Scanner Modal */}
      {showRosterScanner && (
        <RosterScanner
          onApply={handleBatchImportPlayers}
          onClose={() => setShowRosterScanner(false)}
          unitSystem={unitSystem}
          remainingSlots={
            activeTeamId !== 'unassigned'
              ? Math.max(0, TEAM_PLAYER_CAP - players.filter(p => p.teamId === activeTeamId).length)
              : TEAM_PLAYER_CAP
          }
        />
      )}

      {/* Add Team Modal */}
      {isAddingTeam && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
           <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-up">
              <h3 className="font-bold text-lg text-slate-900 mb-2">Create New Team</h3>
              <input 
                 className="w-full p-2 border border-slate-300 rounded-lg mb-4 bg-white text-slate-900"
                 placeholder="Team Name (e.g. Varsity)"
                 value={newTeamName}
                 onChange={e => setNewTeamName(e.target.value)}
                 autoFocus
                 onKeyDown={e => e.key === 'Enter' && handleAddTeam()}
              />
              <div className="flex gap-3">
                 <button onClick={() => setIsAddingTeam(false)} className="flex-1 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium">Cancel</button>
                 <button onClick={handleAddTeam} disabled={!newTeamName.trim()} className="flex-1 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50">Create</button>
              </div>
           </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showBetaTeamLimit && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-up">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3 text-blue-600">
                <Users className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-lg text-slate-900">Beta Team Limit Reached</h3>
              <p className="text-slate-500 mt-1 text-sm leading-relaxed">
                Organizations are limited to <strong className="text-slate-900">2 teams</strong> during the beta.
                Need more? Email us at{' '}
                <a href="mailto:support@sideoutiq.com" className="text-blue-600 font-medium hover:underline">
                  support@sideoutiq.com
                </a>
                .
              </p>
            </div>
            <button
              onClick={() => setShowBetaTeamLimit(false)}
              className="w-full py-2.5 bg-slate-900 text-white font-bold rounded-lg hover:bg-slate-700 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {deleteConfirmation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-up">
                <div className="text-center mb-4">
                    <div className={`w-12 h-12 ${deleteConfirmation.type === 'team' ? 'bg-amber-100' : 'bg-red-100'} rounded-full flex items-center justify-center mx-auto mb-3 ${deleteConfirmation.type === 'team' ? 'text-amber-600' : 'text-red-600'}`}>
                        <AlertTriangle className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-lg text-slate-900">
                      {deleteConfirmation.type === 'team' ? 'Archive Team?' : deleteConfirmation.type === 'player' ? 'Remove Player?' : 'Delete Lineup?'}
                    </h3>
                    <p className="text-slate-500 mt-1 text-sm">
                        {deleteConfirmation.type === 'team' ? (
                          <>
                            <strong className="text-slate-900">{deleteConfirmation.name}</strong> and all its players will be archived. No data is lost — you can restore it anytime from Settings.
                          </>
                        ) : deleteConfirmation.type === 'player' ? (
                          <>
                            How should <strong className="text-slate-900">{deleteConfirmation.name}</strong> be removed from the active roster?
                          </>
                        ) : (
                          <>Are you sure you want to delete <strong className="text-slate-900">{deleteConfirmation.name}</strong>?</>
                        )}
                    </p>
                </div>

                {deleteConfirmation.type === 'player' ? (
                  <div className="flex flex-col gap-2">
                    {seasons.some(s => s.isActive) && (
                      <button
                        onClick={() => executeDeletePlayer(deleteConfirmation.id, true)}
                        className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg shadow-sm text-sm"
                      >
                        Archive to {seasons.find(s => s.isActive)?.name ?? 'Current Season'}
                      </button>
                    )}
                    <button
                      onClick={() => executeDeletePlayer(deleteConfirmation.id, false)}
                      className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg shadow-sm text-sm"
                    >
                      Delete from All Records
                    </button>
                    <button onClick={() => setDeleteConfirmation(null)} className="w-full py-2 text-slate-600 font-bold hover:bg-slate-100 rounded-lg text-sm">Cancel</button>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    <button onClick={() => setDeleteConfirmation(null)} className="flex-1 py-2.5 text-slate-600 font-bold hover:bg-slate-100 rounded-lg">Cancel</button>
                    <button onClick={() => {
                        if (deleteConfirmation.type === 'team') executeDeleteTeam(deleteConfirmation.id, true);
                        else executeDeleteLineup(deleteConfirmation.id);
                    }} className={`flex-1 py-2.5 ${deleteConfirmation.type === 'team' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-red-600 hover:bg-red-700'} text-white font-bold rounded-lg shadow-sm`}>
                      {deleteConfirmation.type === 'team' ? 'Archive' : 'Delete'}
                    </button>
                  </div>
                )}
            </div>
        </div>
      )}

      {/* New Season Modal */}
      {showNewSeasonModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-up space-y-4">
            <div>
              <h3 className="font-bold text-lg text-slate-900">Start New Season</h3>
              <p className="text-slate-500 text-sm mt-1">The current roster will be snapshotted to the outgoing season before starting fresh.</p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-2">Season <span className="text-slate-400 font-normal">(tap to select, tap adjacent to extend)</span></label>
                <div className="flex gap-2">
                  {SEASONS.map((s, i) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => handleSeasonPillClick(i)}
                      className={`flex-1 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                        selectedSeasonIndices.includes(i)
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-2">Year</label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setNewSeasonYear(y => y - 1)}
                    className="w-8 h-8 rounded-lg border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-50 font-bold text-lg leading-none"
                  >−</button>
                  <span className="text-base font-bold text-slate-900 w-12 text-center">{newSeasonYear}</span>
                  <button
                    type="button"
                    onClick={() => setNewSeasonYear(y => y + 1)}
                    className="w-8 h-8 rounded-lg border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-50 font-bold text-lg leading-none"
                  >+</button>
                </div>
              </div>
              {selectedSeasonIndices.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-blue-800 font-semibold text-center">
                  {getSeasonName(selectedSeasonIndices, newSeasonYear)}
                </div>
              )}
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setShowNewSeasonModal(false); setSelectedSeasonIndices([]); setNewSeasonYear(new Date().getFullYear()); setNewSeasonError(null); }} className="flex-1 py-2.5 text-slate-600 font-bold hover:bg-slate-100 rounded-lg">Cancel</button>
              <button
                type="button"
                onClick={handleStartNewSeason}
                disabled={selectedSeasonIndices.length === 0 || isSavingSeason}
                className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg disabled:opacity-50"
              >
                {isSavingSeason ? 'Saving…' : 'Start Season'}
              </button>
            </div>
            {newSeasonError && (
              <p className="text-xs text-red-600 font-medium text-center -mt-1">{newSeasonError}</p>
            )}
          </div>
        </div>
      )}

      {/* Archive Graduated Players Prompt (shown right after a new season starts) */}
      {graduatedPrompt && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md animate-fade-in-up space-y-4 max-h-[90vh] flex flex-col">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                <GraduationCap className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900">Archive graduated players?</h3>
                <p className="text-slate-500 text-sm mt-1">
                  {graduatedPrompt.players.length === 1
                    ? 'This player has a graduation year that has already passed.'
                    : `These ${graduatedPrompt.players.length} players have graduation years that have already passed.`}
                  {' '}Archiving removes them from your active roster only. Their stats and roster history are preserved in <strong className="text-slate-700">{graduatedPrompt.outgoingSeasonName}</strong> and all prior seasons.
                </p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1 border-y border-slate-100 py-2">
              {graduatedPrompt.players.map(p => {
                const checked = selectedGraduatedIds.has(p.id);
                const teamName = teams.find(t => t.id === p.teamId)?.name;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelectedGraduatedIds(prev => {
                      const next = new Set(prev);
                      if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
                      return next;
                    })}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${checked ? 'bg-blue-50/60 hover:bg-blue-50' : 'hover:bg-slate-50'}`}
                  >
                    {checked ? <CheckSquare className="w-5 h-5 text-blue-600 shrink-0" /> : <Square className="w-5 h-5 text-slate-300 shrink-0" />}
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600 border border-slate-200 shrink-0 overflow-hidden">
                      {p.photoUrl ? <img src={p.photoUrl} className="w-full h-full object-cover" /> : (p.jerseyNumber || '')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-slate-800 truncate">{p.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {p.position}{teamName ? ` · ${teamName}` : ''}
                      </div>
                    </div>
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 shrink-0">Class of {p.gradYear}</span>
                  </button>
                );
              })}
            </div>

            <p className="text-xs text-slate-400">
              Not ready? You can archive players any time from Team Management by removing them from the roster and choosing "Archive to season".
            </p>

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => setGraduatedPrompt(null)}
                disabled={isArchivingGraduated}
                className="flex-1 py-2.5 text-slate-600 font-bold hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={handleArchiveGraduated}
                disabled={isArchivingGraduated || selectedGraduatedIds.size === 0}
                className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg disabled:opacity-50"
              >
                {isArchivingGraduated
                  ? 'Archiving…'
                  : `Archive ${selectedGraduatedIds.size} player${selectedGraduatedIds.size === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Views Header */}
      <div className="flex flex-col gap-3">
        {/* Row 1: title + season selector */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Team Management</h2>
            <p className="text-slate-500">Manage rosters, lineups, and player development.</p>
          </div>
          <div className="flex items-center gap-2 pt-1">
            {seasons.length > 0 && (
              <SeasonSelector seasons={seasons} viewingSeason={viewingSeason} onChange={handleViewingSeasonChange} />
            )}
            {isCoach && (
              <button
                type="button"
                onClick={() => setShowNewSeasonModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-lg transition-colors"
              >
                + New Season
              </button>
            )}
          </div>
        </div>

        {/* Historical season banner */}
        {viewingSeason && !viewingSeason.isActive && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
            <p className="text-sm text-amber-800 font-medium">
              Viewing <strong>{viewingSeason.name}</strong> — historical snapshot, read-only.
            </p>
            <button type="button" onClick={() => handleViewingSeasonChange(null)} className="ml-auto text-xs text-amber-700 hover:text-amber-900 font-bold underline shrink-0">
              Back to current
            </button>
          </div>
        )}

      </div>

      {/* Row 3: Team Selection Bar */}
      <div className="flex flex-wrap gap-2 pb-2 items-center">
        {/* Team buttons */}
        {topTeams.map((team) => {
            const isActive = activeTeamId === team.id;
            const isDragTarget = dragOverTeamId === team.id;
            const activeClass = 'bg-blue-600 border-transparent';

            return (
                <button
                    key={team.id}
                    onClick={() => { setActiveTeamId(team.id); onActiveTeamChange?.(team.id); }}
                    onDragOver={(e) => handleDragOverTeam(e, team.id)}
                    onDrop={() => handleDropPlayer(team.id)}
                    className={`
                        px-3 py-2 rounded-full text-sm font-medium border flex items-center gap-1.5 transition-all shrink-0
                        ${isActive ? `${activeClass} text-white shadow-md` : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}
                        ${isDragTarget ? 'ring-2 ring-blue-500 ring-offset-2 scale-105' : ''}
                    `}
                >
                    {team.name}
                </button>
            ); 
        })}

        {/* Dropdown for overflow teams */}
        {otherTeams.length > 0 && (
            <div className="relative">
                <select 
                    className="appearance-none bg-white text-slate-700 border border-slate-200 hover:border-slate-300 px-4 py-2 pr-8 rounded-full text-sm font-medium outline-none cursor-pointer shadow-sm focus:ring-2 focus:ring-blue-500 transition-all"
                    value={topTeams.some(t => t.id === activeTeamId) ? '' : activeTeamId}
                    onChange={(e) => { setActiveTeamId(e.target.value); onActiveTeamChange?.(e.target.value); }}
                >
                    <option value="" disabled hidden>Select other...</option>
                    {otherTeams.map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-2.5 pointer-events-none" />
            </div>
        )}

        
        <button
            onClick={() => setIsAddingTeam(true)}
            className="px-4 py-2 rounded-full text-sm font-medium border border-dashed border-slate-300 text-slate-500 flex items-center gap-1 hover:bg-slate-50 hover:text-slate-700 hover:border-slate-400 transition-colors shrink-0"
        >
            <Plus className="w-3 h-3"/>New Team
        </button>

        {/* Unassigned Players — club mode only, hidden in lineups view, shown last */}
        {viewMode !== 'lineups' && (
            <button
                onClick={() => setActiveTeamId('unassigned')}
                onDragOver={(e) => handleDragOverTeam(e, 'unassigned')}
                onDrop={() => handleDropPlayer('unassigned')}
                className={`
                    px-4 py-2 rounded-full text-sm font-medium border transition-all flex items-center gap-2 shrink-0
                    ${activeTeamId === 'unassigned'
                        ? 'bg-slate-700 text-white border-slate-700 shadow-md'
                        : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}
                    ${dragOverTeamId === 'unassigned' ? 'ring-2 ring-blue-500 ring-offset-2 bg-blue-50' : ''}
                `}
            >
                Unassigned Players
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeTeamId === 'unassigned' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>
                    {players.filter(p => !p.teamId || p.teamId === 'unassigned').length}
                </span>
            </button>
        )}

        {/* Delete selected team — shown whenever a real team is active */}
        {activeTeamId && activeTeamId !== 'unassigned' && activeTeam && (
          <button
            onClick={initiateDeleteTeam}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-400 hover:text-red-600 hover:bg-red-50 border border-slate-200 hover:border-red-200 rounded-lg transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete Team
          </button>
        )}
      </div>

      {/* Row 4: View tabs + Add Player */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-1 bg-white border border-slate-200 p-1 rounded-xl shadow-sm overflow-x-auto">
          <button onClick={() => setViewMode('roster')} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-colors ${viewMode === 'roster' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
            <List className="w-4 h-4" /> Roster
          </button>
          <button onClick={() => setViewMode('depth')} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-colors ${viewMode === 'depth' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
            <Layers className="w-4 h-4" /> Depth
          </button>
          <button onClick={() => setViewMode('lineups')} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-colors ${viewMode === 'lineups' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
            <Shield className="w-4 h-4" /> Lineups
          </button>
          {isCoach && (
            <button onClick={() => setViewMode('notes')} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-colors ${viewMode === 'notes' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
              <StickyNote className="w-4 h-4" /> Notes
            </button>
          )}
        </div>
        {(() => {
          const atCap = activeTeamId !== 'unassigned' && players.filter(p => p.teamId === activeTeamId).length >= TEAM_PLAYER_CAP;
          return (
            <div className="flex items-center gap-2 sm:shrink-0">
              <button
                type="button"
                onClick={() => setShowRosterScanner(true)}
                disabled={atCap}
                title={atCap ? `Teams are limited to ${TEAM_PLAYER_CAP} players` : 'Scan a roster image to import players'}
                className={`px-3 py-2 rounded-lg flex items-center gap-1.5 shadow-sm transition-colors font-medium ${atCap ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200' : 'border border-slate-300 bg-white hover:bg-slate-50 text-slate-700'}`}
              >
                <ScanLine className="w-4 h-4" />
                <span className="text-sm">Scan</span>
              </button>
              <button
                onClick={handleOpenCreatePlayer}
                disabled={atCap}
                title={atCap ? `Teams are limited to ${TEAM_PLAYER_CAP} players` : undefined}
                className={`flex-1 sm:flex-none px-4 py-2 rounded-lg flex items-center justify-center gap-2 shadow-sm transition-colors font-medium ${atCap ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
              >
                <UserPlus className="w-4 h-4"/>
                {atCap ? `Roster Full (${TEAM_PLAYER_CAP})` : 'Add Player'}
              </button>
            </div>
          );
        })()}
      </div>

      {/* VIEW: LINEUPS */}
      {viewMode === 'lineups' && (
        <div className="animate-fade-in-up">
           {visibleTeams.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
                  <Layers className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="font-bold text-slate-700 mb-1">Create a team first</h3>
                <p className="text-slate-400 text-sm mb-6 max-w-xs">Lineups are tied to a specific team. Add your team and then you can build lineups for it.</p>
                <button
                  type="button"
                  onClick={() => setIsAddingTeam(true)}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-2.5 rounded-xl transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" /> Create Team
                </button>
              </div>
           ) : !editingLineup ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                 {/* Quick Build */}
                 <button type="button" onClick={() => setShowQuickBuilder(true)} className="border-2 border-dashed border-blue-300 rounded-xl p-6 flex flex-col items-center justify-center text-blue-500 hover:text-blue-700 hover:border-blue-500 hover:bg-blue-50 transition-all min-h-[200px]">
                    <Wand2 className="w-8 h-8 mb-2" />
                    <span className="font-bold">Quick Build</span>
                    <span className="text-xs mt-1 text-blue-400">Auto-fill from depth chart</span>
                 </button>
                 {/* Manual create */}
                 <button type="button" onClick={handleCreateLineup} className="border-2 border-dashed border-slate-300 rounded-xl p-6 flex flex-col items-center justify-center text-slate-400 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-all min-h-[200px]">
                    <Plus className="w-8 h-8 mb-2" />
                    <span className="font-bold">Create New Lineup</span>
                    <span className="text-xs mt-1 text-slate-400">Build manually</span>
                 </button>
                 {/* Scan from sheet — dev/beta only */}
                 {canSeeDevFeatures && (
                   <button type="button" onClick={handleScanIntoNewLineup} className="border-2 border-dashed border-violet-300 rounded-xl p-6 flex flex-col items-center justify-center text-violet-500 hover:text-violet-700 hover:border-violet-500 hover:bg-violet-50 transition-all min-h-[200px]">
                     <ScanLine className="w-8 h-8 mb-2" />
                     <span className="font-bold">Scan from Sheet</span>
                     <span className="text-xs mt-1 text-violet-400">Import from paper lineup</span>
                   </button>
                 )}
                 {teamLineups.map(lineup => {
                    const pos = lineup.positions as Record<string, string>;
                    const slotRoman: Record<string, string> = { p1: 'I', p2: 'II', p3: 'III', p4: 'IV', p5: 'V', p6: 'VI' };
                    const posInitialMap: Record<string, string> = {
                      'Setter': 'S', 'Outside Hitter': 'OH', 'Right Side': 'Oppo', 'Opposite': 'Oppo',
                      'Middle Blocker': 'MB', 'Libero': 'L', 'DS': 'DS', 'Unknown': '?'
                    };
                    const getCell = (slot: string) => {
                      const p = currentTeamPlayers.find(pl => pl.id === pos[slot]);
                      return {
                        num: p ? (p.jerseyNumber ? `#${p.jerseyNumber}` : '') : '—',
                        name: p ? p.name.split(' ')[0] : '',
                        pos: p ? (posInitialMap[p.position] || p.position.slice(0, 2)) : '',
                      };
                    };
                    // I top-left (serving), VI top-center, V top-right / II bot-left, III bot-center, IV bot-right
                    const backRow = ['p1', 'p6', 'p5'];
                    const frontRow = ['p2', 'p3', 'p4'];
                    return (
                    <div key={lineup.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-all cursor-pointer group" onClick={() => handleEditLineup(lineup)}>
                        <div className="p-3 border-b border-slate-100 flex justify-between items-center gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                                <button
                                  onClick={(e) => handleToggleLineupFavorite(e, lineup.id)}
                                  title={lineup.isFavorite ? 'Remove favorite (default lineup)' : 'Set as favorite (auto-loads in GameTime)'}
                                  className="shrink-0"
                                >
                                  <Star className={`w-4 h-4 transition-colors ${lineup.isFavorite ? 'fill-amber-400 text-amber-400' : 'text-slate-300 hover:text-amber-400'}`} />
                                </button>
                                <h3 className="font-bold text-slate-800 truncate">{lineup.name}</h3>
                                <span className={`shrink-0 text-[10px] font-black px-1.5 py-0.5 rounded-full ${lineup.system === '6-2' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-600'}`}>{lineup.system || '5-1'}</span>
                            </div>
                            <button onClick={(e) => initiateDeleteLineup(e, lineup.id)} className="text-slate-400 hover:text-red-500 p-1 shrink-0"><Trash2 className="w-4 h-4"/></button>
                        </div>
                        {/* Mini court preview: back row top, front row bottom, NET below */}
                        <div className="px-3 pt-2 pb-2 bg-slate-50">
                          <div className="border border-slate-200 rounded-t-lg overflow-hidden text-[10px]">
                            {/* Back row */}
                            <div className="grid grid-cols-3 divide-x divide-slate-200 bg-white">
                              {backRow.map(slot => {
                                const cell = getCell(slot);
                                const isServer = slot === 'p1';
                                return (
                                  <div key={slot} className={`py-1.5 px-1 flex flex-col items-center gap-0.5 ${isServer ? 'bg-amber-50' : ''}`}>
                                    <span className="text-[8px] font-bold text-slate-400">{slotRoman[slot]}</span>
                                    <span className={`font-black ${isServer ? 'text-amber-700' : 'text-slate-800'}`}>{cell.num}</span>
                                    <span className="text-slate-500 truncate w-full text-center leading-tight">{cell.name}</span>
                                    {cell.pos && <span className={`text-[8px] font-bold px-1 rounded ${isServer ? 'text-amber-600' : 'text-slate-400'}`}>{cell.pos}</span>}
                                  </div>
                                );
                              })}
                            </div>
                            {/* Thin separator between rows */}
                            <div className="h-px bg-slate-200" />
                            {/* Front row */}
                            <div className="grid grid-cols-3 divide-x divide-slate-200 bg-blue-50/60">
                              {frontRow.map(slot => {
                                const cell = getCell(slot);
                                return (
                                  <div key={slot} className="py-1.5 px-1 flex flex-col items-center gap-0.5">
                                    <span className="text-[8px] font-bold text-slate-400">{slotRoman[slot]}</span>
                                    <span className="font-black text-blue-800">{cell.num}</span>
                                    <span className="text-slate-500 truncate w-full text-center leading-tight">{cell.name}</span>
                                    {cell.pos && <span className="text-[8px] font-bold text-blue-500 px-1 rounded">{cell.pos}</span>}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          {/* NET indicator */}
                          <div className="flex items-center gap-1 rounded-b-lg border border-t-0 border-slate-200 bg-slate-200 px-2 py-0.5">
                            <div className="flex-1 h-px bg-slate-400" />
                            <span className="text-[9px] font-black text-slate-500 tracking-widest uppercase">Net</span>
                            <div className="flex-1 h-px bg-slate-400" />
                          </div>
                        </div>
                    </div>
                    );
                 })}
              </div>
           ) : (
              <div className="bg-slate-100 rounded-xl p-4">
                  {/* Lineup Editor UI */}

                  {/* ── Top bar: name + action buttons always visible ── */}
                  <div className="flex items-start gap-3 mb-4">
                      <div className="flex-1 min-w-0">
                          <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Lineup Name</label>
                          <input
                              className={`w-full p-3 border rounded-lg text-xl font-bold outline-none text-slate-800 bg-white focus:ring-2 focus:ring-blue-500 ${saveError ? 'border-red-500 ring-1 ring-red-500' : 'border-slate-300'}`}
                              placeholder="e.g. Set 1 Starter"
                              value={editingLineup.name}
                              onChange={e => {
                                  setEditingLineup({...editingLineup, name: e.target.value});
                                  if (e.target.value) setSaveError(false);
                              }}
                          />
                          {saveError && <p className="text-[10px] text-red-500 font-bold mt-1">Name is required to save.</p>}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0 pt-6">
                          {dbSaveError && <p className="text-[11px] text-red-500 font-bold text-right max-w-[160px]">{dbSaveError}</p>}
                          <div className="flex gap-2">
                              <button onClick={() => { setEditingLineup(null); setEditingRotSub(null); setIsSuggestedRotSub(false); setDbSaveError(null); }} className="px-4 py-2.5 text-slate-600 font-bold hover:bg-white rounded-lg transition-colors text-sm">Cancel</button>
                              <button onClick={handleSaveLineup} disabled={isSavingLineup} className="px-6 py-2.5 bg-blue-600 text-white font-bold rounded-lg shadow-sm hover:bg-blue-700 flex items-center gap-2 text-sm">
                                  {isSavingLineup && <Loader2 className="w-4 h-4 animate-spin"/>}
                                  Save
                              </button>
                          </div>
                      </div>
                  </div>

                  {/* ── Options: system, dual-role setters, libero ── */}
                  <div className="mb-6">
                      <div className="w-full md:w-auto flex flex-col gap-2">
                              {/* ── Offensive system toggle ── */}
                              <div>
                                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Offensive System</label>
                                  <div className="flex rounded-lg border border-slate-300 overflow-hidden">
                                      {(['5-1', '6-2'] as const).map(sys => (
                                          <button
                                              key={sys}
                                              type="button"
                                              onClick={() => {
                                                  setEditingLineup({
                                                      ...editingLineup,
                                                      system: sys,
                                                      dualRoleSetterIds: sys === '5-1' ? [] : (editingLineup.dualRoleSetterIds || []),
                                                  });
                                              }}
                                              className={`flex-1 px-4 py-2 text-sm font-bold transition-colors ${(editingLineup.system || '5-1') === sys ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                                          >
                                              {sys}
                                          </button>
                                      ))}
                                  </div>
                              </div>
                              {/* ── 6-2 dual-role setter toggles ── */}
                              {(editingLineup.system || '5-1') === '6-2' && (() => {
                                  const setterSlots = Object.entries(editingLineup.positions || {})
                                      .filter(([, pid]) => {
                                          const p = players.find(pl => pl.id === pid);
                                          return p?.position === 'Setter';
                                      });
                                  if (setterSlots.length === 0) return null;
                                  return (
                                      <div>
                                          <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Setters — plays all the way around?</label>
                                          <div className="flex flex-col gap-1">
                                              {setterSlots.map(([slot, pid]) => {
                                                  const p = players.find(pl => pl.id === pid);
                                                  if (!p) return null;
                                                  const isChecked = (editingLineup.dualRoleSetterIds || []).includes(pid);
                                                  return (
                                                      <button
                                                          key={pid}
                                                          type="button"
                                                          onClick={() => {
                                                              const current = editingLineup.dualRoleSetterIds || [];
                                                              setEditingLineup({
                                                                  ...editingLineup,
                                                                  dualRoleSetterIds: isChecked
                                                                      ? current.filter(id => id !== pid)
                                                                      : [...current, pid],
                                                              });
                                                          }}
                                                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-semibold transition-colors text-left ${isChecked ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                                                      >
                                                          <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${isChecked ? 'border-amber-500 bg-amber-500' : 'border-slate-300'}`}>
                                                              {isChecked && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                                                          </span>
                                                          <span>#{p.jerseyNumber} {p.name} <span className="font-normal text-xs opacity-70">({slot.toUpperCase()})</span></span>
                                                      </button>
                                                  );
                                              })}
                                          </div>
                                      </div>
                                  );
                              })()}
                              {/* ── Libero count toggle (only when app settings allow 2) ── */}
                              {allowTwoLiberos && (
                                  <div>
                                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Liberos in lineup</label>
                                      <div className="flex rounded-lg border border-slate-300 overflow-hidden">
                                          {([1, 2] as const).map(n => (
                                              <button
                                                  key={n}
                                                  type="button"
                                                  onClick={() => {
                                                      setLineupLiberoCount(n);
                                                      if (n === 1) {
                                                          // Drop slot-1 libero if switching to single
                                                          setLiberoConfig(prev => prev.slice(0, 1));
                                                      }
                                                  }}
                                                  className={`flex-1 px-4 py-2 text-sm font-bold transition-colors ${lineupLiberoCount === n ? 'bg-purple-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                                              >
                                                  {n} Libero{n === 2 ? 's' : ''}
                                              </button>
                                          ))}
                                      </div>
                                  </div>
                              )}
                              {/* ── Libero Slots ── */}
                              {[0, ...(lineupLiberoCount === 2 ? [1] : [])].map(slot => {
                                  const la = liberoConfig[slot];
                                  const slotLabel = lineupLiberoCount === 2 ? (slot === 0 ? 'Libero 1 (L1)' : 'Libero 2 (L2)') : 'Libero';
                                  return (
                                      <div key={slot} className="flex gap-2 items-end">
                                          <div>
                                              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{slotLabel}</label>
                                              <select
                                                  className={`p-2 border border-slate-300 rounded-lg outline-none font-bold cursor-pointer ${la ? 'bg-purple-50 text-purple-800 border-purple-300' : 'bg-white text-slate-600'}`}
                                                  value={la?.liberoId || ''}
                                                  onChange={(e) => {
                                                      const pid = e.target.value;
                                                      setLiberoConfig(prev => {
                                                          const next = [...prev];
                                                          if (!pid) {
                                                              next.splice(slot, 1);
                                                              return next;
                                                          }
                                                          const pos = editingLineup.positions || {};
                                                          const allMBIds = currentTeamPlayers
                                                              .filter(p => p.id !== pid && p.position === 'Middle Blocker')
                                                              .map(p => p.id);
                                                          // Two-libero: each covers one MB; single: covers both MBs
                                                          // Fall back to roster MBs if lineup slots aren't filled yet
                                                          const defaultReplaces = lineupLiberoCount === 2
                                                              ? (() => {
                                                                  const slotPlayer = slot === 0 ? pos.p3 : pos.p6;
                                                                  if (slotPlayer) return [slotPlayer];
                                                                  const takenByOther = next.filter((_, i) => i !== slot).flatMap(l => l?.replacesPlayerIds || []);
                                                                  const avail = allMBIds.filter(id => !takenByOther.includes(id));
                                                                  return avail.length > 0 ? [avail[0]] : [];
                                                              })()
                                                              : (() => {
                                                                  const fromSlots = [pos.p3, pos.p6].filter(Boolean) as string[];
                                                                  return fromSlots.length > 0 ? fromSlots : allMBIds.slice(0, 2);
                                                              })();
                                                          next[slot] = {
                                                              liberoId: pid,
                                                              replacesPlayerIds: defaultReplaces,
                                                              servesForPlayerId: next[slot]?.servesForPlayerId,
                                                          };
                                                          return next;
                                                      });
                                                  }}
                                              >
                                                  <option value="">—</option>
                                                  {(() => {
                                                      const parseNum = (n?: string) => parseInt((n || '').replace(/\D/g, '') || '0') || 0;
                                                      const eligible = currentTeamPlayers.filter(p => !liberoConfig.some((l, i) => i !== slot && l.liberoId === p.id));
                                                      const liberos = eligible.filter(p => p.position === 'Libero').sort((a, b) => parseNum(a.jerseyNumber) - parseNum(b.jerseyNumber));
                                                      const others = eligible.filter(p => p.position !== 'Libero');
                                                      return [...liberos, ...others].map(p => (
                                                          <option key={p.id} value={p.id}>
                                                              #{p.jerseyNumber} {p.name} {p.position === 'Libero' ? '(L)' : ''}
                                                          </option>
                                                      ));
                                                  })()}
                                              </select>
                                          </div>
                                          {/* Configure button: opens libero options modal */}
                                          {la && (() => {
                                              const pos = editingLineup.positions || {};
                                              const pairs: { label: string; keys: [string, string] }[] = [
                                                  { label: 'I & IV', keys: ['p1', 'p4'] },
                                                  { label: 'II & V', keys: ['p2', 'p5'] },
                                                  { label: 'III & VI', keys: ['p3', 'p6'] },
                                              ];
                                              const replacesIds = la.replacesPlayerIds || [];
                                              const activePair = pairs.find(pair => {
                                                  const pairIds = [pair.keys[0], pair.keys[1]].map(k => (pos as any)[k]).filter(Boolean);
                                                  return pairIds.length > 0 && pairIds.every((id: string) => replacesIds.includes(id)) && replacesIds.every((id: string) => pairIds.includes(id));
                                              });
                                              const servesForPlayer = la.servesForPlayerId ? currentTeamPlayers.find(p => p.id === la.servesForPlayerId) : undefined;
                                              const singlePlayer = !activePair && replacesIds.length === 1
                                                  ? currentTeamPlayers.find(p => p.id === replacesIds[0])
                                                  : undefined;
                                              const isConfigured = !!activePair || !!singlePlayer || replacesIds.length > 0;
                                              const configLabel = activePair
                                                  ? `${activePair.label}${servesForPlayer && servesForPlayer.jerseyNumber ? ` · #${servesForPlayer.jerseyNumber}` : ''}`
                                                  : singlePlayer
                                                  ? `#${singlePlayer.jerseyNumber || '?'} ${singlePlayer.name.split(' ')[0]}${servesForPlayer && servesForPlayer.jerseyNumber ? ` · #${servesForPlayer.jerseyNumber}` : ''}`
                                                  : `${replacesIds.length} players`;
                                              return (
                                                  <button
                                                      type="button"
                                                      onClick={() => setLiberoOptionsModal({ slot, replacesPlayerIds: la.replacesPlayerIds || [], servesForPlayerId: la.servesForPlayerId || '' })}
                                                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-[11px] font-bold transition-colors ${isConfigured ? 'bg-purple-50 text-purple-700 border-purple-300 hover:bg-purple-100' : 'bg-white text-slate-500 border-slate-300 hover:border-purple-400 hover:text-purple-600'}`}
                                                  >
                                                      <Settings2 className="w-3.5 h-3.5 shrink-0" />
                                                      <span>
                                                          {isConfigured ? configLabel : 'Set options'}
                                                      </span>
                                                  </button>
                                              );
                                          })()}
                                      </div>
                                  );
                              })}
                          </div>
                      </div>

                  {/* Court — matches GameTime's edit mode court appearance */}
                  <div className="mb-8">
                      <div className="grid grid-cols-3 grid-rows-2 gap-px bg-slate-200 border-[8px] border-blue-400 p-[5px]">
                          <div className="bg-white"><LineupCard pos="p1" label="I"   assignedId={editingLineup.positions?.p1 || ''} players={currentTeamPlayers} onClick={() => setLineupSwapModal({ pos: 'p1' })} /></div>
                          <div className="bg-white"><LineupCard pos="p6" label="VI"  assignedId={editingLineup.positions?.p6 || ''} players={currentTeamPlayers} onClick={() => setLineupSwapModal({ pos: 'p6' })} /></div>
                          <div className="bg-white"><LineupCard pos="p5" label="V"   assignedId={editingLineup.positions?.p5 || ''} players={currentTeamPlayers} onClick={() => setLineupSwapModal({ pos: 'p5' })} /></div>
                          <div className="bg-white"><LineupCard pos="p2" label="II"  assignedId={editingLineup.positions?.p2 || ''} players={currentTeamPlayers} onClick={() => setLineupSwapModal({ pos: 'p2' })} /></div>
                          <div className="bg-white"><LineupCard pos="p3" label="III" assignedId={editingLineup.positions?.p3 || ''} players={currentTeamPlayers} onClick={() => setLineupSwapModal({ pos: 'p3' })} /></div>
                          <div className="bg-white"><LineupCard pos="p4" label="IV"  assignedId={editingLineup.positions?.p4 || ''} players={currentTeamPlayers} onClick={() => setLineupSwapModal({ pos: 'p4' })} /></div>
                      </div>
                      {/* NET — matches GameTime standard orientation */}
                      <div className="w-full h-7 shrink-0 flex flex-row">
                          <div className="w-2.5 bg-black shrink-0" />
                          <div className="flex-1" style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent 0px, transparent 5px, #111 5px, #111 6px), repeating-linear-gradient(90deg, transparent 0px, transparent 5px, #111 5px, #111 6px)", backgroundColor: '#d1d5db' }} />
                          <div className="w-2.5 bg-black shrink-0" />
                      </div>
                  </div>

                  {/* ── Scheduled Substitutions ── */}
                  <div className="mt-6 bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                      <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2">
                        <Repeat className="w-4 h-4 text-purple-500" />
                        Scheduled Substitutions
                      </h3>
                      {!editingRotSub && (
                        <button
                          onClick={() => setEditingRotSub({ triggerType: 'front_row', ignoreFirstTime: false })}
                          className="text-xs font-bold text-purple-600 hover:text-purple-800 flex items-center gap-1 transition-colors"
                        >
                          <Plus className="w-3.5 h-3.5" /> Add Sub
                        </button>
                      )}
                    </div>

                    {/* List of configured subs */}
                    {(editingLineup.rotationalSubstitutions || []).length === 0 && !editingRotSub && (
                      <p className="px-4 py-3 text-xs text-slate-400">No scheduled subs configured. Add one to auto-prompt sub swaps during a game.</p>
                    )}
                    {(editingLineup.rotationalSubstitutions || []).map(sub => {
                      const playerOff = currentTeamPlayers.find(p => p.id === sub.playerOffId);
                      const playerOn = currentTeamPlayers.find(p => p.id === sub.playerOnId);
                      const triggerLabel: Record<string, string> = {
                        front_row: 'Front Row',
                        back_row: 'Back Row',
                        after_serving: 'After Serving',
                        rotation_number: `Rotation ${sub.triggerRotation}`,
                        serve_receive_rotation: `SR Rot ${sub.triggerRotation}`,
                      };
                      return (
                        <div key={sub.id} className="px-4 py-2.5 flex items-center gap-2 border-b border-slate-100 last:border-0 text-sm">
                          <span className="font-semibold text-slate-700">{playerOff?.jerseyNumber ? `#${playerOff.jerseyNumber} ` : ''}{playerOff?.name?.split(' ')[0] || 'Unknown'}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="text-[11px] font-bold text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full whitespace-nowrap">{triggerLabel[sub.triggerType]}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="font-semibold text-emerald-700">{playerOn?.jerseyNumber ? `#${playerOn.jerseyNumber} ` : ''}{playerOn?.name?.split(' ')[0] || 'Unknown'}</span>
                          {sub.ignoreFirstTime && <span className="text-[10px] text-slate-400 italic ml-1">skip 1st</span>}
                          <button
                            onClick={() => setEditingLineup(prev => prev ? {
                              ...prev,
                              rotationalSubstitutions: (prev.rotationalSubstitutions || []).filter(s => s.id !== sub.id)
                            } : null)}
                            className="ml-auto p-1 text-slate-300 hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}

                    {/* Inline add form */}
                    {editingRotSub && (
                      <div className="px-4 py-3 border-t border-slate-100 flex flex-col gap-3">
                        {/* Suggestion banner */}
                        {isSuggestedRotSub && (
                          <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-xs text-indigo-700 font-bold">
                            <Repeat className="w-3.5 h-3.5 shrink-0" />
                            Suggested reverse sub — edit or dismiss as needed
                          </div>
                        )}
                        {/* Player OFF */}
                        <div className="flex items-center gap-3">
                          <label className="text-xs font-bold text-slate-500 w-16 shrink-0">Sub Out</label>
                          <select
                            value={editingRotSub.playerOffId || ''}
                            onChange={e => setEditingRotSub(p => ({ ...p, playerOffId: e.target.value }))}
                            className="flex-1 text-sm border border-slate-300 rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-purple-400"
                          >
                            <option value="">— Select player —</option>
                            {(() => {
                              if (isSuggestedRotSub) return currentTeamPlayers;
                              const onCourt = new Set(Object.values(editingLineup.positions || {}).filter(Boolean));
                              // Also include players who will be on court via other scheduled subs
                              (editingLineup.rotationalSubstitutions || []).forEach(s => onCourt.add(s.playerOnId));
                              return currentTeamPlayers.filter(pl => onCourt.has(pl.id));
                            })().map(p => (
                              <option key={p.id} value={p.id}>{p.jerseyNumber ? `#${p.jerseyNumber} ` : ''}{p.name}</option>
                            ))}
                          </select>
                        </div>
                        {/* Trigger type */}
                        <div className="flex items-center gap-3">
                          <label className="text-xs font-bold text-slate-500 w-16 shrink-0">Trigger</label>
                          <select
                            value={editingRotSub.triggerType || 'front_row'}
                            onChange={e => {
                              const tt = e.target.value as RotationalSubstitution['triggerType'];
                              const needsRotation = tt === 'rotation_number' || tt === 'serve_receive_rotation';
                              setEditingRotSub(p => ({ ...p, triggerType: tt, triggerRotation: needsRotation ? (p?.triggerRotation || 1) : undefined }));
                            }}
                            className="flex-1 text-sm border border-slate-300 rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-purple-400"
                          >
                            <option value="front_row">A) When they reach front row</option>
                            <option value="back_row">B) When they reach the back row (before serving)</option>
                            <option value="after_serving">C) After they serve</option>
                            <option value="rotation_number">D1) At specific rotation #</option>
                            <option value="serve_receive_rotation">D2) Serve receive at rotation #</option>
                          </select>
                        </div>
                        {/* Trigger rotation # */}
                        {(editingRotSub.triggerType === 'rotation_number' || editingRotSub.triggerType === 'serve_receive_rotation') && (
                          <div className="flex items-center gap-3">
                            <label className="text-xs font-bold text-slate-500 w-16 shrink-0">Rotation #</label>
                            <select
                              value={editingRotSub.triggerRotation || 1}
                              onChange={e => setEditingRotSub(p => ({ ...p, triggerRotation: parseInt(e.target.value) }))}
                              className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-purple-400"
                            >
                              {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>Rotation {n}</option>)}
                            </select>
                          </div>
                        )}
                        {/* Player ON */}
                        <div className="flex items-center gap-3">
                          <label className="text-xs font-bold text-slate-500 w-16 shrink-0">Sub In</label>
                          <select
                            value={editingRotSub.playerOnId || ''}
                            onChange={e => setEditingRotSub(p => ({ ...p, playerOnId: e.target.value }))}
                            className="flex-1 text-sm border border-slate-300 rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-purple-400"
                          >
                            <option value="">— Select bench player —</option>
                            {(() => {
                              if (isSuggestedRotSub) return currentTeamPlayers;
                              const onCourt = new Set(Object.values(editingLineup.positions || {}).filter(Boolean));
                              // Also include starters who will be on the bench via other scheduled subs (subbed off)
                              const willBeBenched = new Set((editingLineup.rotationalSubstitutions || []).map(s => s.playerOffId));
                              return currentTeamPlayers.filter(p => !onCourt.has(p.id) || willBeBenched.has(p.id));
                            })().map(p => (
                              <option key={p.id} value={p.id}>{p.jerseyNumber ? `#${p.jerseyNumber} ` : ''}{p.name}</option>
                            ))}
                          </select>
                        </div>
                        {/* Ignore first time */}
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={editingRotSub.ignoreFirstTime || false}
                            onChange={e => setEditingRotSub(p => ({ ...p, ignoreFirstTime: e.target.checked }))}
                            className="rounded border-slate-300 text-purple-600"
                          />
                          <span className="text-xs text-slate-600">Ignore first trigger (skip the first time this fires)</span>
                        </label>
                        {/* Buttons */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => { setEditingRotSub(null); setIsSuggestedRotSub(false); }}
                            className="px-3 py-1.5 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                          >
                            {isSuggestedRotSub ? 'Skip' : 'Cancel'}
                          </button>
                          <button
                            onClick={() => {
                              if (!editingRotSub.playerOffId || !editingRotSub.playerOnId) return;
                              const newSub: RotationalSubstitution = {
                                id: `rs-${Date.now()}`,
                                playerOffId: editingRotSub.playerOffId,
                                playerOnId: editingRotSub.playerOnId,
                                triggerType: editingRotSub.triggerType || 'front_row',
                                triggerRotation: editingRotSub.triggerRotation,
                                ignoreFirstTime: editingRotSub.ignoreFirstTime || false,
                              };
                              setEditingLineup(prev => prev ? {
                                ...prev,
                                rotationalSubstitutions: [...(prev.rotationalSubstitutions || []), newSub]
                              } : null);
                              const reverseTrigger: Record<string, RotationalSubstitution['triggerType']> = {
                                front_row: 'back_row', back_row: 'front_row', after_serving: 'front_row',
                                serve_receive_rotation: 'rotation_number',
                              };
                              const rt = reverseTrigger[newSub.triggerType];
                              if (rt && !isSuggestedRotSub) {
                                setIsSuggestedRotSub(true);
                                setEditingRotSub({ playerOffId: newSub.playerOnId, playerOnId: newSub.playerOffId, triggerType: rt, triggerRotation: newSub.triggerRotation, ignoreFirstTime: false });
                              } else {
                                setIsSuggestedRotSub(false);
                                setEditingRotSub(null);
                              }
                            }}
                            disabled={!editingRotSub.playerOffId || !editingRotSub.playerOnId || (
                              (editingRotSub.triggerType === 'rotation_number' || editingRotSub.triggerType === 'serve_receive_rotation') && !editingRotSub.triggerRotation
                            )}
                            className="px-4 py-1.5 text-sm font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            Add Sub
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

              </div>
           )}
        </div>
      )}

      {/* VIEW: HISTORICAL ROSTER (season snapshot) */}
      {viewMode === 'roster' && viewingSeason && !viewingSeason.isActive && (
        <div className="bg-white rounded-xl shadow-sm border border-amber-200 overflow-hidden animate-fade-in-up">
          <div className="p-2 bg-amber-50 border-b border-amber-200 flex justify-between items-center text-xs text-amber-700">
            <span className="px-2 font-semibold">Roster snapshot from {viewingSeason.name}</span>
          </div>
          {(!viewingSeason.rosterSnapshot || viewingSeason.rosterSnapshot.length === 0) ? (
            <div className="px-4 py-8 text-center text-slate-400 italic text-sm">No roster snapshot was saved for this season.</div>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-slate-500 font-medium text-xs uppercase">
                <tr>
                  <th className="px-4 py-3">No.</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Position</th>
                  <th className="px-4 py-3">Height</th>
                  <th className="px-4 py-3">Grad</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {viewingSeason.rosterSnapshot.map((p: Player) => (
                  <tr key={p.id} className="text-slate-700">
                    <td className="px-4 py-3 font-bold text-slate-600">{p.jerseyNumber}</td>
                    <td className="px-4 py-3 font-bold text-slate-900 flex items-center gap-2">
                      {p.photoUrl && <img src={p.photoUrl} className="w-9 h-9 rounded-full object-cover border border-slate-200" />}
                      {p.name}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium">{p.position}</span>
                        {p.secondaryPosition && <span className="bg-slate-50 border border-slate-200 px-2 py-0.5 rounded text-xs text-slate-400">{p.secondaryPosition}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">{formatHeight(p.heightCm, unitSystem)}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{p.gradYear || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* VIEW: ROSTER */}
      {viewMode === 'roster' && !(viewingSeason && !viewingSeason.isActive) && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden animate-fade-in-up">
              <div className="p-2 bg-slate-50 border-b border-slate-200 flex justify-between items-center text-xs text-slate-500">
                  <span className="px-2">Click any player row to edit details.</span>
              </div>
              <table className="w-full text-left">
                  <thead className="bg-slate-50 text-slate-500 font-medium text-xs uppercase">
                      <tr>
                          <th className="px-4 py-3 cursor-pointer hover:text-blue-600 hover:bg-slate-100 transition-colors select-none" onClick={() => setSortBy('number')}>
                              <div className="flex items-center gap-1">
                                  No.
                                  {sortBy === 'number' && <ArrowDown className="w-3 h-3 text-blue-500" />}
                              </div>
                          </th>
                          <th className="px-4 py-3 cursor-pointer hover:text-blue-600 hover:bg-slate-100 transition-colors select-none" onClick={() => setSortBy('name')}>
                              <div className="flex items-center gap-1">
                                  Name
                                  {sortBy === 'name' && <ArrowDown className="w-3 h-3 text-blue-500" />}
                              </div>
                          </th>
                          <th className="px-4 py-3 cursor-pointer hover:text-blue-600 hover:bg-slate-100 transition-colors select-none" onClick={() => setSortBy('position')}>
                              <div className="flex items-center gap-1">
                                  Position
                                  {sortBy === 'position' && <ArrowDown className="w-3 h-3 text-blue-500" />}
                              </div>
                          </th>
                          <th className="px-4 py-3">Height</th>
                          {activeTeamId === 'unassigned' && <th className="px-4 py-3">Notes</th>}
                          <th className="px-4 py-3">{activeTeamId === 'unassigned' && visibleTeams.length > 0 ? 'Team' : ''}</th>
                      </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                      {sortedPlayers.length === 0 ? (
                          <tr><td colSpan={activeTeamId === 'unassigned' ? 6 : 5} className="px-4 py-8 text-center text-slate-400 italic">No players found in this team.</td></tr>
                      ) : sortedPlayers.map(p => (
                          <tr
                            key={p.id}
                            className="hover:bg-slate-50 group transition-colors"
                          >
                              <td className="px-4 py-3 font-bold text-slate-600">
                                <button type="button" className="w-full text-left" onClick={() => handleOpenEditPlayer(p)}>{p.jerseyNumber}</button>
                              </td>
                              <td className="px-4 py-3">
                                <button type="button" className="flex items-center gap-2 w-full text-left font-bold text-slate-900" onClick={() => handleOpenEditPlayer(p)}>
                                  {p.photoUrl && <img src={p.photoUrl} className="w-9 h-9 rounded-full object-cover border border-slate-200" />}
                                  {p.name}
                                </button>
                              </td>
                              <td className="px-4 py-3 text-slate-600 text-sm">
                                <button type="button" className="flex items-center gap-1 flex-wrap w-full text-left" onClick={() => handleOpenEditPlayer(p)}>
                                  <span className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium">{p.position}</span>
                                  {p.secondaryPosition
                                    ? <span className="bg-slate-50 border border-slate-200 px-2 py-0.5 rounded text-xs text-slate-400">{p.secondaryPosition}</span>
                                    : <span className="text-[10px] text-slate-300 italic">+ also plays</span>
                                  }
                                </button>
                              </td>
                              <td className="px-4 py-3 text-slate-500 text-sm">
                                <button type="button" className="w-full text-left" onClick={() => handleOpenEditPlayer(p)}>{formatHeight(p.heightCm, unitSystem)}</button>
                              </td>
                              {activeTeamId === 'unassigned' && (
                                <td className="px-4 py-3 align-top">
                                  <div className="flex flex-col gap-1 min-w-[200px]">
                                    <textarea
                                      key={p.id}
                                      rows={2}
                                      defaultValue={p.notes || ''}
                                      placeholder="Add notes…"
                                      ref={el => {
                                        if (!el) return;
                                        if (expandedNotes.has(p.id)) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; }
                                        else { el.style.height = ''; }
                                      }}
                                      onClick={e => e.stopPropagation()}
                                      onInput={e => { if (expandedNotes.has(p.id)) { e.currentTarget.style.height = 'auto'; e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`; } }}
                                      onBlur={e => handleSavePlayerNotes(p.id, e.currentTarget.value)}
                                      className={`w-full min-h-[3rem] resize-none text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 bg-white hover:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder:text-slate-300 ${expandedNotes.has(p.id) ? '' : 'overflow-hidden'}`}
                                    />
                                    <button
                                      type="button"
                                      onClick={e => { e.stopPropagation(); toggleNotesExpand(p.id); }}
                                      className="flex items-center justify-center gap-1 w-full py-1 text-[11px] font-medium text-slate-400 hover:text-blue-600 hover:bg-slate-50 rounded-md transition-colors"
                                      title={expandedNotes.has(p.id) ? 'Collapse notes' : 'Expand notes'}
                                    >
                                      {expandedNotes.has(p.id)
                                        ? <><ChevronUp className="w-3.5 h-3.5" /> Collapse</>
                                        : <><ChevronDown className="w-3.5 h-3.5" /> Expand</>}
                                    </button>
                                  </div>
                                </td>
                              )}
                              <td className="px-4 py-3">
                                  {activeTeamId === 'unassigned' && visibleTeams.length > 0 ? (
                                    <select
                                      className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 bg-white hover:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                                      defaultValue=""
                                      onClick={e => e.stopPropagation()}
                                      onChange={e => { if (e.target.value) handleAssignPlayerToTeam(p.id, e.target.value); }}
                                    >
                                      <option value="" disabled>Move to team…</option>
                                      {sortedVisibleTeams.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <button
                                      onClick={(e) => initiateDeletePlayer(e, p.id)}
                                      className="text-slate-400 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                      title="Delete Player"
                                    >
                                      <Trash2 className="w-4 h-4"/>
                                    </button>
                                  )}
                              </td>
                          </tr>
                      ))}
                  </tbody>
              </table>
          </div>
      )}

      {/* VIEW: DEPTH CHART */}
      {viewMode === 'depth' && (
        visibleTeams.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
              <BarChart2 className="w-7 h-7 text-blue-400" />
            </div>
            <h3 className="font-bold text-slate-700 mb-1">Create a team first</h3>
            <p className="text-slate-400 text-sm mb-6 max-w-xs">The depth chart is organized by team. Add your team to start ranking players by position.</p>
            <button
              type="button"
              onClick={() => setIsAddingTeam(true)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-2.5 rounded-xl transition-colors text-sm"
            >
              <Plus className="w-4 h-4" /> Create Team
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-fade-in-up">
              {POSITIONS.map(pos => {
                  const aliases = POSITION_ALIASES[pos] || [pos];
                  const playersInPos = sortedPlayers.filter(p => aliases.includes(p.position));
                  const isActiveDropTarget = dragOverPosition === pos;
                  const posLabel = pos === 'Unknown' ? 'Unspecified' : pos;

                  return (
                      <div
                        key={pos}
                        className={`bg-white rounded-xl border-2 shadow-sm overflow-hidden flex flex-col transition-colors min-h-[200px] ${isActiveDropTarget ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200'}`}
                        onDragOver={(e) => handleDepthDragOver(e, pos)}
                        onDrop={(e) => handleDepthDrop(e, pos)}
                      >
                          <div className={`p-3 border-b font-bold text-sm flex justify-between items-center ${isActiveDropTarget ? 'bg-blue-100 border-blue-200 text-blue-800' : 'bg-slate-50 border-slate-100 text-slate-700'}`}>
                              <span>{posLabel}</span>
                              <span className="text-xs bg-white border border-slate-200 px-2 py-0.5 rounded-full text-slate-500">{playersInPos.length}</span>
                          </div>
                          <div className="p-2 space-y-1 flex-1">
                              {playersInPos.length === 0 ? (
                                  <div className="text-center py-8 text-slate-400 text-sm border-2 border-dashed border-slate-100 rounded-lg">Drag here</div>
                              ) : (
                                  playersInPos.map((p, rankIdx) => {
                                      const isDragging = draggedPlayerId === p.id;
                                      const showLineAbove = dragOverPlayerId === p.id && dragOverHalf === 'top' && !isDragging;
                                      const showLineBelow = dragOverPlayerId === p.id && dragOverHalf === 'bottom' && !isDragging;
                                      return (
                                        <div key={p.id} className="relative">
                                          {showLineAbove && (
                                            <div className="absolute top-0 inset-x-2 h-0.5 bg-blue-500 rounded-full z-10 -translate-y-px pointer-events-none" />
                                          )}
                                          <div
                                            className={`
                                                flex items-center gap-3 p-2 rounded-lg cursor-pointer group border-2 transition-colors
                                                ${isDragging ? 'opacity-40 bg-slate-100 border-dashed border-slate-300' : 'hover:bg-slate-50 border-transparent'}
                                            `}
                                            onClick={() => handleOpenEditPlayer(p)}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, p.id)}
                                            onDragEnd={handleDragEnd}
                                            onDragOver={(e) => handlePlayerDragOver(e, pos, p.id)}
                                            onDrop={(e) => handleDepthDrop(e, pos, p.id)}
                                          >
                                            <div className="text-slate-300 group-hover:text-slate-400 cursor-grab active:cursor-grabbing">
                                                <GripVertical className="w-4 h-4" />
                                            </div>
                                            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-600 border border-slate-200 shrink-0 overflow-hidden">
                                                {p.photoUrl ? <img src={p.photoUrl} className="w-full h-full object-cover" /> : (p.jerseyNumber || '')}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5">
                                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${rankIdx === 0 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                                                        #{rankIdx + 1}
                                                    </span>
                                                    <span className="text-sm font-bold text-slate-800 truncate">{p.name}</span>
                                                </div>
                                                <div className="flex items-center gap-1 mt-0.5">
                                                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 shrink-0">{getPosAbbreviation(p.position)}</span>
                                                    {p.secondaryPosition && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 shrink-0">{getPosAbbreviation(p.secondaryPosition)}</span>}
                                                    {p.gradYear && <span className="text-[9px] text-slate-400 ml-0.5">'{String(p.gradYear).slice(-2)}</span>}
                                                </div>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500" />
                                          </div>
                                          {showLineBelow && (
                                            <div className="absolute bottom-0 inset-x-2 h-0.5 bg-blue-500 rounded-full z-10 translate-y-px pointer-events-none" />
                                          )}
                                        </div>
                                      );
                                  })
                              )}
                          </div>
                      </div>
                  )
              })}
          </div>
        )
      )}

      {/* VIEW: COACH NOTES */}
      {viewMode === 'notes' && (
          <div className="max-w-3xl mx-auto space-y-6 animate-fade-in-up">
              {!activeTeam || activeTeamId === 'unassigned' ? (
                  <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200 text-slate-400">
                      Select a specific team to view or add notes.
                  </div>
              ) : (
                  <>
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex gap-2">
                        <input 
                            className="flex-1 bg-white border-blue-200 border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="New Section Title (e.g. Offensive Systems)"
                            value={newSectionTitle}
                            onChange={e => setNewSectionTitle(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && addNoteSection()}
                        />
                        <button onClick={addNoteSection} disabled={!newSectionTitle.trim()} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-blue-700 disabled:opacity-50">Add Section</button>
                    </div>

                    <div className="space-y-4">
                        {(activeTeam.coachNotes || []).map(section => (
                            <div key={section.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="p-3 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                                    <h4 className="font-bold text-slate-700">{section.title}</h4>
                                    <button onClick={() => deleteNoteSection(section.id)} className="text-slate-400 hover:text-red-500 p-1"><Trash2 className="w-4 h-4"/></button>
                                </div>
                                <textarea 
                                    className="w-full p-4 min-h-[120px] text-sm text-slate-700 outline-none resize-y"
                                    placeholder="Add notes here..."
                                    value={section.content}
                                    onChange={(e) => updateNoteContent(section.id, e.target.value)}
                                />
                            </div>
                        ))}
                        {(activeTeam.coachNotes || []).length === 0 && (
                            <div className="text-center py-12 text-slate-400 italic">No notes yet. Add a section above.</div>
                        )}
                    </div>
                  </>
              )}
          </div>
      )}

      {/* Libero Options Modal */}
      {liberoOptionsModal && editingLineup && (() => {
          const { slot } = liberoOptionsModal;
          const la = liberoConfig[slot];
          if (!la) return null;
          const libPlayer = currentTeamPlayers.find(p => p.id === la.liberoId);
          const slotLabel = lineupLiberoCount === 2 ? (slot === 0 ? 'Libero 1' : 'Libero 2') : 'Libero';
          const isTwoLiberoMode = lineupLiberoCount === 2;
          const libName = libPlayer ? `${libPlayer.jerseyNumber ? `#${libPlayer.jerseyNumber} ` : ''}${libPlayer.name}` : slotLabel;
          const pos = (editingLineup.positions || {}) as Record<string, string>;
          const currentIds = liberoOptionsModal.replacesPlayerIds;
          return (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
                  <div className="bg-white rounded-2xl shadow-2xl p-5 w-full max-w-sm">
                      <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center shrink-0">
                              <ArrowLeftRight className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                              <h3 className="font-bold text-lg text-slate-900">{libName} — {slotLabel}</h3>
                              <p className="text-xs text-slate-500">{isTwoLiberoMode ? 'Select which player(s) this libero covers' : 'Choose the rotation pair this libero replaces'}</p>
                          </div>
                      </div>

                      <div className="mb-4">
                          {isTwoLiberoMode ? (
                              /* Two-libero: pick one player from full roster */
                              <>
                                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Replaces player (select one)</label>
                                  <div className="flex flex-col gap-1.5 max-h-52 overflow-y-auto pr-0.5">
                                      {(() => {
                                          const parseNum = (n?: string) => parseInt((n || '').replace(/\D/g, '') || '0') || 0;
                                          const otherReplacedIds = liberoConfig.filter((_, i) => i !== slot).flatMap(l => l?.replacesPlayerIds || []);
                                          const posToRoman: Record<string, string> = { p1: 'I', p2: 'II', p3: 'III', p4: 'IV', p5: 'V', p6: 'VI' };
                                          const playerRotLabel: Record<string, string> = {};
                                          Object.entries(pos).forEach(([k, pid]) => { if (pid && posToRoman[k]) playerRotLabel[pid] = posToRoman[k]; });
                                          return currentTeamPlayers
                                              .filter(p => p.id !== la.liberoId && p.position !== 'Libero')
                                              .sort((a, b) => {
                                                  const aMB = a.position === 'Middle Blocker' ? 0 : 1;
                                                  const bMB = b.position === 'Middle Blocker' ? 0 : 1;
                                                  return aMB !== bMB ? aMB - bMB : parseNum(a.jerseyNumber) - parseNum(b.jerseyNumber);
                                              })
                                              .map(player => {
                                                  const isSelected = currentIds[0] === player.id;
                                                  const takenByOther = otherReplacedIds.includes(player.id);
                                                  const rotLabel = playerRotLabel[player.id];
                                                  return (
                                                      <button
                                                          key={player.id}
                                                          type="button"
                                                          onClick={() => {
                                                              setLiberoOptionsModal(prev => {
                                                                  if (!prev) return prev;
                                                                  const next = isSelected ? [] : [player.id];
                                                                  const keepServes = next.includes(prev.servesForPlayerId);
                                                                  return { ...prev, replacesPlayerIds: next, servesForPlayerId: keepServes ? prev.servesForPlayerId : '' };
                                                              });
                                                          }}
                                                          className={`w-full flex items-center gap-2 p-2.5 rounded-xl border-2 text-left transition-all ${isSelected ? 'border-purple-400 bg-purple-50 text-purple-900' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300'}`}
                                                      >
                                                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${isSelected ? 'border-purple-500' : 'border-slate-300'}`}>
                                                              {isSelected && <div className="w-2 h-2 rounded-full bg-purple-500" />}
                                                          </div>
                                                          <span className="text-sm font-bold">{player.jerseyNumber ? `#${player.jerseyNumber} ` : ''}{player.name}</span>
                                                          <span className="text-xs text-slate-400 ml-auto">{rotLabel ? `Rot. ${rotLabel}` : takenByOther ? 'Used by other L' : (player.position || '')}</span>
                                                      </button>
                                                  );
                                              });
                                      })()}
                                  </div>
                              </>
                          ) : (
                              /* Single libero: pick up to 2 players from full roster */
                              <>
                                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Replaces (select 1–2 players)</label>
                                  <div className="flex flex-col gap-1.5 max-h-52 overflow-y-auto pr-0.5">
                                      {(() => {
                                          const parseNum = (n?: string) => parseInt((n || '').replace(/\D/g, '') || '0') || 0;
                                          const posToRoman: Record<string, string> = { p1: 'I', p2: 'II', p3: 'III', p4: 'IV', p5: 'V', p6: 'VI' };
                                          const playerRotLabel: Record<string, string> = {};
                                          Object.entries(pos).forEach(([k, pid]) => { if (pid && posToRoman[k]) playerRotLabel[pid] = posToRoman[k]; });
                                          return currentTeamPlayers
                                              .filter(p => p.id !== la.liberoId && p.position !== 'Libero')
                                              .sort((a, b) => {
                                                  const aMB = a.position === 'Middle Blocker' ? 0 : 1;
                                                  const bMB = b.position === 'Middle Blocker' ? 0 : 1;
                                                  return aMB !== bMB ? aMB - bMB : parseNum(a.jerseyNumber) - parseNum(b.jerseyNumber);
                                              })
                                              .map(player => {
                                                  const isSelected = currentIds.includes(player.id);
                                                  const canToggle = isSelected || currentIds.length < 2;
                                                  const rotLabel = playerRotLabel[player.id];
                                                  return (
                                                      <button
                                                          key={player.id}
                                                          type="button"
                                                          disabled={!canToggle}
                                                          onClick={() => {
                                                              setLiberoOptionsModal(prev => {
                                                                  if (!prev) return prev;
                                                                  const next = isSelected
                                                                      ? prev.replacesPlayerIds.filter(id => id !== player.id)
                                                                      : [...prev.replacesPlayerIds, player.id];
                                                                  const keepServes = next.includes(prev.servesForPlayerId);
                                                                  return { ...prev, replacesPlayerIds: next, servesForPlayerId: keepServes ? prev.servesForPlayerId : '' };
                                                              });
                                                          }}
                                                          className={`w-full flex items-center gap-2 p-2.5 rounded-xl border-2 text-left transition-all ${isSelected ? 'border-purple-400 bg-purple-50 text-purple-900' : canToggle ? 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300' : 'border-slate-100 bg-slate-50 text-slate-400 opacity-50 cursor-not-allowed'}`}
                                                      >
                                                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${isSelected ? 'border-purple-500 bg-purple-500' : 'border-slate-300'}`}>
                                                              {isSelected && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>}
                                                          </div>
                                                          <span className="text-sm font-bold">{player.jerseyNumber ? `#${player.jerseyNumber} ` : ''}{player.name}</span>
                                                          <span className="text-xs text-slate-400 ml-auto">{rotLabel ? `Rot. ${rotLabel}` : (player.position || '')}</span>
                                                      </button>
                                                  );
                                              });
                                      })()}
                                  </div>
                              </>
                          )}
                      </div>

                      {liberoOptionsModal.replacesPlayerIds.length > 0 && (
                          <div className="border-t border-slate-100 pt-3 mb-4">
                              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Libero serves for (optional)</label>
                              <div className="flex flex-col gap-1.5">
                                  {([
                                      { id: '', label: 'Does not serve' },
                                      ...liberoOptionsModal.replacesPlayerIds.map(pid => {
                                          const p = currentTeamPlayers.find(pl => pl.id === pid);
                                          return p ? { id: p.id, label: `${p.jerseyNumber ? `#${p.jerseyNumber} ` : ''}${p.name}` } : null;
                                      }).filter((x): x is { id: string; label: string } => x !== null)
                                  ]).map(opt => (
                                      <button
                                          key={opt.id}
                                          type="button"
                                          onClick={() => setLiberoOptionsModal(prev => prev ? { ...prev, servesForPlayerId: opt.id } : prev)}
                                          className={`w-full flex items-center gap-2 p-2.5 rounded-xl border-2 text-left transition-all ${liberoOptionsModal.servesForPlayerId === opt.id ? 'border-amber-400 bg-amber-50 text-amber-900' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300'}`}
                                      >
                                          {opt.id === '' ? <X className="w-4 h-4 text-slate-400 shrink-0" /> : <Lock className="w-4 h-4 text-amber-500 shrink-0" />}
                                          <span className="text-sm font-bold">{opt.label}</span>
                                      </button>
                                  ))}
                              </div>
                          </div>
                      )}

                      <div className="flex gap-2">
                          <button type="button" onClick={() => setLiberoOptionsModal(null)} className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold rounded-xl text-sm transition-colors">Cancel</button>
                          <button type="button" onClick={handleSaveLiberoOptions} className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl text-sm transition-colors">Save</button>
                      </div>
                  </div>
              </div>
          );
      })()}

      {lineupSwapModal && editingLineup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg animate-fade-in-up">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg text-slate-900">
                {(editingLineup.positions as any)?.[lineupSwapModal.pos] ? 'Swap Player' : 'Assign Player'}
              </h3>
              <button type="button" onClick={() => setLineupSwapModal(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto">
              {(() => {
                const pos = lineupSwapModal.pos;
                const positions = (editingLineup.positions || {}) as Record<string, string>;
                const currentId = positions[pos] || '';
                const currentPlayer = currentTeamPlayers.find(p => p.id === currentId);
                const slotPosition = currentPlayer?.position || '';
                const inLineupIds = new Set(Object.entries(positions).filter(([k]) => k !== pos).map(([, v]) => v).filter(Boolean));
                const parseNum = (p: Player) => parseInt((p.jerseyNumber || '').replace(/\D/g, '') || '0') || 0;

                return currentTeamPlayers
                  .filter(p => p.id !== currentId)
                  .sort((a, b) => {
                    const aIn = inLineupIds.has(a.id);
                    const bIn = inLineupIds.has(b.id);
                    const aSame = slotPosition && a.position === slotPosition ? 0 : 1;
                    const bSame = slotPosition && b.position === slotPosition ? 0 : 1;
                    const aScore = (aIn ? 10 : 0) + aSame;
                    const bScore = (bIn ? 10 : 0) + bSame;
                    if (aScore !== bScore) return aScore - bScore;
                    return parseNum(a) - parseNum(b);
                  })
                  .map(p => {
                    const isInLineup = inLineupIds.has(p.id);
                    const isSamePos = !!(slotPosition && p.position === slotPosition);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => { handleLineupAssignment(pos, p.id); setLineupSwapModal(null); }}
                        className={`flex flex-col items-center justify-center p-3 border rounded-lg transition-colors relative ${
                          isInLineup
                            ? 'border-amber-300 bg-amber-50 hover:bg-amber-100'
                            : isSamePos
                              ? 'border-blue-300 bg-blue-50 hover:bg-blue-100'
                              : 'border-slate-200 hover:bg-blue-50'
                        }`}
                      >
                        {isInLineup && (
                          <span className="absolute top-1 right-1 text-[8px] font-black text-amber-600 leading-none">LINEUP</span>
                        )}
                        <span className="font-black text-xl text-slate-800">{p.jerseyNumber}</span>
                        <span className="text-xs font-bold text-slate-600 truncate w-full text-center">{p.name}</span>
                        {p.position && <span className="text-[10px] text-slate-400 mt-0.5">{p.position}</span>}
                      </button>
                    );
                  });
              })()}
            </div>
            {(editingLineup.positions as any)?.[lineupSwapModal.pos] && (
              <button
                type="button"
                onClick={() => { handleLineupAssignment(lineupSwapModal.pos, ''); setLineupSwapModal(null); }}
                className="mt-3 w-full py-2.5 bg-slate-100 border border-slate-200 text-slate-700 font-bold text-sm rounded-xl hover:bg-slate-200 transition-colors"
              >
                Clear Slot
              </button>
            )}
            <button
              type="button"
              onClick={() => setLineupSwapModal(null)}
              className="mt-2 w-full py-2.5 text-slate-500 font-bold text-sm hover:bg-slate-50 rounded-xl transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showLineupScanner && editingLineup && (
        <LineupSheetScanner
          players={currentTeamPlayers}
          onApply={(positions, liberoId) => {
            setEditingLineup(prev => prev ? { ...prev, positions } : prev);
            if (liberoId) setLiberoConfig([{ liberoId, replacesPlayerIds: [] }]);
            setShowLineupScanner(false);
          }}
          onClose={() => setShowLineupScanner(false)}
        />
      )}

      {showQuickBuilder && setLineups && (
        <QuickLineupBuilder
          isOpen={showQuickBuilder}
          onClose={() => setShowQuickBuilder(false)}
          teamPlayers={currentTeamPlayers}
          organizationId={organizationId}
          teamId={activeTeamId}
          setLineups={setLineups}
          allowTwoLiberos={allowTwoLiberos}
          onAllowTwoLiberosChange={onAllowTwoLiberosChange}
        />
      )}
    </div>
  );
};

export default TeamManagement;
